/**
 * Zod schemas for every strategic data file (spec §4.9), the command union (spec §11.3), the views and the
 * handoff records (spec §10.1, §11.2, §12, §13) and the v1 save (spec §14). Single-file rules live here
 * (a recipe consumes fuel cells, hull costs name component wares only, the ware and faction order, the M3
 * type map, the Wire slots); references across files are checked by `data.ts`.
 *
 * Data objects are strict: an unknown key (a typo in a tuning file) is an error, not silently dropped.
 */
import { z } from 'zod'
import type { AnchorId, Archetype } from '../galaxy/types'
import {
  EV,
  CAUSE_KIND_NAMES,
  FACTION,
  FACTION_IDS,
  FIELD_KIND_NAMES,
  GROUP_KIND_NAMES,
  LEG_PURPOSE_NAMES,
  LINE_STATUS_NAMES,
  NODE_KIND_NAMES,
  ROLE_IDS,
  SITE_STAGE_NAMES,
  WIRE_KINDS,
  CRIME_NAMES,
  GATE_STAGE_NAMES,
  MEASURE_NAMES,
  RELATION_NAMES,
  REQUEST_KIND_NAMES,
  STAGE_NAMES,
  STATE_KIND_NAMES,
  TRAIT_NAMES,
  HULL_CLASS_NAMES,
  ACTION_NAMES,
  type CharterCommand,
  type CharterSave,
  type CharterVote,
  type FactionRequest,
  type FactionRequestView,
  type WorksDeliverCommand,
  type StrategicSaveV2,
  type AwayReport,
  type ConstructionSite,
  type DayRollupRecord,
  type ClaimView,
  type GalaxyEconomyView,
  type GroupRecord,
  type LocalAgent,
  type LocalFleet,
  type LocalDelta,
  type LocalManifest,
  type LocalStep,
  type LocalSync,
  type MarketRow,
  type NodeRef,
  type OffscreenWreck,
  type SavedOffscreenWreck,
  WRECK_MAKER_IDS,
  WRECK_CLAIM,
  WRECK_OWNER_NONE,
  type PriceCause,
  type PriceCauseTag,
  type ShipOrder,
  type SiteView,
  type StationView,
  type StrategicCommand,
  type StrategicSaveV1,
  type SystemEconomyView,
  type WireItem,
  type WireKind,
  SHIP_CLASS_NAMES,
  ORDER_NAMES,
  ROE_NAMES,
  STOP_NAMES,
  SELL_POLICY_NAMES,
  PATROL_RULE_NAMES,
  SKILL_NAMES,
  CAPTAIN_TRAIT_NAMES,
  STATION_TYPE_NAMES,
  MILESTONE_NAMES,
  DOCKING_RIGHTS_NAMES,
  PRICE_POLICY_NAMES,
  TRADE_PERMISSION_NAMES,
  ORDER_PARAM_NAMES,
  ROLE_PARAM_COUNT,
  ORDER_PARAM,
  type AwayFleetLine,
  type AwayFleetSummary,
  type FleetLogEntry,
  type FleetSave,
  type LiveryColors,
  type LogoRecipe,
  type M6Command,
  type MemorialEntry,
  type OrderParamName,
  type StandingOrder,
  PLAYER_FACTION_ID,
  STORY_BALLOT_STANCES,
  STORY_CLAIM_STATES,
  STORY_MISSION_IDS,
  STORY_REGISTRANTS,
  STORY_RETALIATION_ROWS,
  STORY_SHIP_ROLES,
  STORY_WIRE_OUTLETS,
  type M7Command,
  type MarkClaimCommand,
  type ClaimedShipCommand,
  type RegisterClaimedShipCommand,
  type ScrapClaimedShipCommand,
  type SetHullFlawsCommand,
  type StorySave,
} from './types'
import type { PlayerDrawCommand } from './economy/market'
import { COMPONENT_TYPES, COMPONENT_WARE_IDS, RAW_WARE_IDS, WARE_IDS, WARE_TIERS } from './wares'
import { HULL_BAND_IDS } from './hullLines'

// ---------------------------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------------------------

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const posInt = z.number().int().positive()
const nonNegInt = z.number().int().nonnegative()
const idString = z.string().min(1)
const comment = z.string().optional()
const vec3 = z.tuple([z.number(), z.number(), z.number()])

export const wareIdSchema = z.enum(WARE_IDS)
export const factionIdSchema = z.enum(FACTION_IDS)
export const roleIdSchema = z.enum(ROLE_IDS)
export const componentTypeSchema = z.enum(COMPONENT_TYPES)

/** Tonnes per ware (a subset of the wares). */
const wareQty = z.partialRecord(wareIdSchema, positive)
const wareAmounts = z.partialRecord(wareIdSchema, z.number())

function sumPositive(weights: Record<string, number | undefined>): boolean {
  let total = 0
  for (const k of Object.keys(weights)) total += weights[k] ?? 0
  return total > 0
}

/** Faction weights (at least one positive). */
const factionWeights = z.partialRecord(factionIdSchema, nonNegative).refine(sumPositive, { message: 'expected at least one positive weight' })
/** Weights keyed by an id checked elsewhere (hull ids). */
const idWeights = z.record(idString, nonNegative).refine(sumPositive, { message: 'expected at least one positive weight' })

/** An inclusive [min, max] integer range. */
const intRange = z.tuple([nonNegInt, nonNegInt]).refine(([lo, hi]) => lo <= hi, { message: 'expected [min, max] with min ≤ max' })
/** A [min, max] real range. */
const numRange = z.tuple([nonNegative, nonNegative]).refine(([lo, hi]) => lo <= hi, { message: 'expected [min, max] with min ≤ max' })
const unitRange = z.tuple([unit, unit]).refine(([lo, hi]) => lo <= hi, { message: 'expected [min, max] with min ≤ max' })

function isPowerOfTwo(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0
}

// The galaxy lists (kept here: `src/data/galaxyConfig.ts` is outside the strategic import boundary).
export const ARCHETYPES = [
  'mining-belt',
  'ice-field',
  'gas-harvest',
  'industrial-hub',
  'trade-crossroads',
  'derelict-graveyard',
  'raider-haven',
  'descendant-enclave',
  'tetherstone-vein',
  'chokepoint',
] as const satisfies readonly Archetype[]
export const ANCHOR_IDS = [
  'home-gate',
  'authority-seat',
  'megacorp-hq',
  'descendant-capital',
  'guardian-base',
  'okafor-hideout',
] as const satisfies readonly AnchorId[]
/** Compile-time proof the lists above cover the galaxy unions (`true` or a type error). */
export type GalaxyListsComplete = [Exclude<Archetype, (typeof ARCHETYPES)[number]> | Exclude<AnchorId, (typeof ANCHOR_IDS)[number]>] extends [never]
  ? true
  : never
export const GALAXY_LISTS_COMPLETE: GalaxyListsComplete = true

/** M3 layout station types (M3 draft `StationType`; plan I0 cross-checks them against M3's code). */
export const M3_STATION_TYPES = ['hub', 'refinery', 'scrapyard', 'trade-post', 'outpost', 'habitat', 'patrol-base'] as const
/** M2's three home stations (M2 draft `stations.json`). */
export const M2_STATION_IDS = ['hub', 'refinery', 'scrapyard'] as const
export type M2StationId = (typeof M2_STATION_IDS)[number]

// ---------------------------------------------------------------------------------------------
// wares.json (spec §4.1, §5.1)
// ---------------------------------------------------------------------------------------------

/**
 * M10 P2d1 (spec §7 P2d): the hold a ware rides in. Read by nobody until P5a6 (the hold split), so no sim value
 * depends on it; a row without one parses as `container`.
 */
export const CARGO_CLASSES = ['bulk', 'container', 'liquid', 'sealed'] as const
export type CargoClass = (typeof CARGO_CLASSES)[number]
export const cargoClassSchema = z.enum(CARGO_CLASSES)

export const waresFileSchema = z.strictObject({
  comment,
  market: z
    .strictObject({
      minMult: positive,
      maxMult: positive,
      buyMarkup: positive,
      playerTolerance: unit,
      standingSwing: unit,
      causeMinDelta: unit,
      trendMinDelta: unit,
      baselineDays: positive,
    })
    .refine((m) => m.minMult < m.maxMult, { message: 'market.minMult must be below maxMult' }),
  wares: z
    .array(
      z.strictObject({
        id: wareIdSchema,
        name: idString,
        tier: z.enum(WARE_TIERS),
        basePrice: positive,
        icon: idString,
        cargoClass: cargoClassSchema.default('container'),
      }),
    )
    .superRefine((wares, ctx) => {
      const ids = wares.map((w) => w.id)
      if (ids.length !== WARE_IDS.length || ids.some((id, i) => id !== WARE_IDS[i])) {
        ctx.addIssue({ code: 'custom', message: `wares must be exactly [${WARE_IDS.join(', ')}] in this order` })
        return
      }
      for (const w of wares) {
        const raw = (RAW_WARE_IDS as readonly string[]).includes(w.id)
        const component = (COMPONENT_WARE_IDS as readonly string[]).includes(w.id)
        if (raw !== (w.tier === 'raw')) ctx.addIssue({ code: 'custom', message: `ware ${w.id}: tier raw iff one of the M2 raw wares (RAW_WARE_IDS)` })
        if (component !== (w.tier === 'component')) ctx.addIssue({ code: 'custom', message: `ware ${w.id}: tier component iff a component ware` })
      }
    }),
})
export type WaresFile = z.infer<typeof waresFileSchema>

// ---------------------------------------------------------------------------------------------
// modules.json (spec §4.2, §6.1-§6.4, §5.7)
// ---------------------------------------------------------------------------------------------

export const recipeSchema = z
  .strictObject({
    id: idString,
    inputs: wareQty,
    outputs: wareQty,
    cycleS: positive,
    /** R2-T1: a ware on both sides is netted, so the cycle draws that input from its own output (no start stock needed). */
    selfFed: z.boolean().default(false),
    /** R2-T1 (D-335 D3): a multi-output line runs while any output has room; a full output is clamped at its cap. */
    partialOutput: z.boolean().default(false),
    /**
     * 5(e) (MC-e2, v7 migration calls §2.4): the outputs are owner-only (TRADE_BIT.ownerOnly, as collect.ownerOnlyResale):
     * only the station's owner faction takes them; no trader, supplier, constructor or pilot of another faction buys them.
     */
    ownerOnly: z.boolean().default(false),
    /**
     * 5(e) (MC-e7b4, D-TBD): the outputs carry TRADE_BIT.noPilot: every AI faction's traders, suppliers and constructors
     * buy them, but the pilot and the player corporation do not (unless it owns the station). Replaces `ownerOnly` on the
     * refractory alloy line (RD6 keeps the gate import terminal as the pilot's source).
     */
    noPilot: z.boolean().default(false),
    /**
     * 5(e) (MC-e2, D-TBD): the line is dormant, not starved, while this input is what it lacks: the stall raises no
     * stationStarved, adds no starved seconds (line, station day or metrics) and so never reaches the Wire. Starving on
     * any other input counts as usual. For a line whose ore only some regions hold (the refractory alloy).
     */
    dormantOn: wareIdSchema.optional(),
  })
  .refine((r) => r.dormantOn === undefined || (r.inputs[r.dormantOn] ?? 0) > 0, { message: 'dormantOn must name one of the recipe inputs' })
  .refine((r) => (r.inputs.fuel_cells ?? 0) >= 1, { message: 'every recipe consumes at least 1 fuel_cells (design §3.2)' })
  .refine((r) => Object.keys(r.outputs).length > 0, { message: 'a recipe needs at least one output' })
export type RecipeDef = z.infer<typeof recipeSchema>

const productionModuleSchema = z.strictObject({
  id: idString,
  kind: z.literal('production'),
  recipes: z.array(recipeSchema).min(1),
  /** Owner-only collection (Tethys refineries collect tetherstone, spec §4.2). */
  collect: z
    .strictObject({ owner: factionIdSchema, wares: z.array(wareIdSchema).min(1), capacity: positive, ownerOnlyResale: z.boolean() })
    .optional(),
})
const habitatModuleSchema = z
  .strictObject({
    id: idString,
    kind: z.literal('habitat'),
    /** Per level per period. */
    consumes: wareQty,
    /** Fulfilment weights (food 0.5, water 0.3, medical 0.2). */
    weights: wareQty,
    periodS: positive,
    workersPerLevel: positive,
    satisfactionRate: unit,
    startSatisfaction: unit,
  })
  .refine((h) => Object.keys(h.weights).every((k) => k in h.consumes), { message: 'habitat weights must name consumed wares' })
const shipyardModuleSchema = z.strictObject({ id: idString, kind: z.literal('shipyard'), slotsPerLevel: posInt, buysPerLevel: wareQty })
const tradeModuleSchema = z.strictObject({ id: idString, kind: z.literal('trade'), basket: z.array(wareIdSchema).min(1), capacity: positive })
const exportModuleSchema = z.strictObject({ id: idString, kind: z.literal('export'), buys: wareQty, sells: wareQty })
const fenceModuleSchema = z.strictObject({ id: idString, kind: z.literal('fence'), priceMult: positive, capacity: positive })
export const moduleSchema = z.discriminatedUnion('kind', [
  productionModuleSchema,
  habitatModuleSchema,
  shipyardModuleSchema,
  tradeModuleSchema,
  exportModuleSchema,
  fenceModuleSchema,
])
export type ModuleDef = z.infer<typeof moduleSchema>
export type ModuleKind = ModuleDef['kind']

export const modulesFileSchema = z
  .strictObject({
    comment,
    stockCycles: positive,
    targetFill: unit,
    workforce: z.strictObject({ needPerLevel: positive, maxBonus: nonNegative }),
    starvedEventS: positive,
    /**
     * ST-1a2 (D9): whether a blocked line (an output full) adds its stalled seconds to the station's `dayStarvedS`,
     * which Bust's `starvedLineShare` reads. Absent = true = today; false counts only lines short of an input.
     */
    starvedCountsBlocked: z.boolean().optional(),
    modules: z.array(moduleSchema).min(1),
    /** Ware yield per M2 component at condition 1 (every component type listed; `{}` = none). */
    salvageYields: z.record(componentTypeSchema, wareQty),
  })
  .superRefine((file, ctx) => {
    const ids = new Set<string>()
    const recipes = new Set<string>()
    for (const m of file.modules) {
      if (ids.has(m.id)) ctx.addIssue({ code: 'custom', message: `duplicate module id ${m.id}` })
      ids.add(m.id)
      if (m.kind !== 'production') continue
      for (const r of m.recipes) {
        if (recipes.has(r.id)) ctx.addIssue({ code: 'custom', message: `duplicate recipe id ${r.id}` })
        recipes.add(r.id)
      }
    }
  })
export type ModulesFile = z.infer<typeof modulesFileSchema>

// ---------------------------------------------------------------------------------------------
// templates.json (spec §4.3)
// ---------------------------------------------------------------------------------------------

export const templateSchema = z
  .strictObject({
    id: idString,
    noun: idString,
    assetId: idString,
    /** The M3 layout station type this template plays (◆), null for M4-only templates. */
    m3Type: z.enum(M3_STATION_TYPES).nullable(),
    m2Id: z.enum(M2_STATION_IDS).nullable(),
    modules: z.array(z.strictObject({ module: idString, scales: z.boolean() })).min(1),
    anchorModule: z.strictObject({ asteroid: idString, ice: idString, gas: idString, other: idString }).optional(),
    basket: z.array(wareIdSchema).min(1).optional(),
    levels: intRange.refine(([lo]) => lo >= 1, { message: 'levels start at 1' }),
    /** buildCost[level − 1]. */
    buildCost: z.array(wareQty).min(1),
    buildDays: positive,
    componentMult: positive.nullable(),
    dockable: z.boolean(),
    hidden: z.boolean(),
  })
  .refine((t) => t.buildCost.length === t.levels[1], { message: 'buildCost needs one entry per level up to the max level' })
export type TemplateDef = z.infer<typeof templateSchema>

export const templatesFileSchema = z
  .strictObject({
    comment,
    /** M3 station type → template id (the ◆ templates). */
    typeMap: z.record(z.enum(M3_STATION_TYPES), idString),
    templates: z.array(templateSchema).min(1),
  })
  .superRefine((file, ctx) => {
    const ids = new Set<string>()
    for (const t of file.templates) {
      if (ids.has(t.id)) ctx.addIssue({ code: 'custom', message: `duplicate template id ${t.id}` })
      ids.add(t.id)
    }
    for (const type of M3_STATION_TYPES) {
      const target = file.typeMap[type]
      if (!ids.has(target)) ctx.addIssue({ code: 'custom', message: `typeMap.${type} names unknown template "${target}"` })
    }
    for (const t of file.templates) {
      if (t.m3Type !== null && file.typeMap[t.m3Type] !== t.id) {
        ctx.addIssue({ code: 'custom', message: `template ${t.id}: m3Type ${t.m3Type} maps to "${file.typeMap[t.m3Type]}" in typeMap` })
      }
    }
  })
export type TemplatesFile = z.infer<typeof templatesFileSchema>

// ---------------------------------------------------------------------------------------------
// economy-seed.json (spec §4.4, §4.5, §6.5)
// ---------------------------------------------------------------------------------------------

const archetypeEntrySchema = z.strictObject({ template: idString, count: intRange, owner: factionIdSchema.optional() })
const anchorEntrySchema = z.strictObject({
  template: idString,
  owner: factionIdSchema,
  count: posInt.optional(),
  level: posInt.optional(),
  m2Id: z.enum(M2_STATION_IDS).optional(),
  /** SUP-2b1 (D-751): the seeding revision that adds this entry (absent = every world); a world below it skips it. */
  since: posInt.optional(),
  /**
   * M10 P4a7c (D-857 ruling): seed this entry only in a world where this faction owns no yard (no shipyard station)
   * after the regular seeding of every system; read once, before any such entry is placed and before the level-2
   * guarantee (`economy/seed.ts seedConditionalAnchors`). Absent = seeded with the system's mix as usual.
   */
  onlyIfNoYardOf: factionIdSchema.optional(),
}).refine((e) => e.onlyIfNoYardOf === undefined || e.m2Id === undefined, { message: 'an onlyIfNoYardOf entry cannot play an M2 station' })
export const PLACEMENT_AT = ['field', 'body', 'relay', 'gate', 'poi', 'free'] as const
const placementRuleSchema = z
  .strictObject({ at: z.enum(PLACEMENT_AT), fieldKinds: z.array(z.enum(FIELD_KIND_NAMES)).min(1).optional() })
  .refine((r) => (r.at === 'field') === (r.fieldKinds !== undefined), { message: "fieldKinds is required for (and only for) at: 'field'" })
const regionWeightsSchema = z.strictObject({ gate: factionWeights, mid: factionWeights, deep: factionWeights })
/**
 * M10 P4a2c (spec §4.9, review M4): one level-2 seed guarantee entry. Worlds at revision `since` or above raise the
 * lowest-index seeded yard of each listed maker that seeded only level-1 yards to level 2. The makers are frozen
 * per entry (never derived from hull-lines at seeding), so a later L line needs its own entry at the next revision.
 */
const levelTwoGuaranteeEntrySchema = z.strictObject({
  since: posInt,
  makers: z
    .array(factionIdSchema)
    .min(1)
    .refine((m) => new Set(m).size === m.length, { message: 'duplicate maker' }),
})

export const economySeedFileSchema = z
  .strictObject({
    comment,
    /**
     * SUP-2b1 (D-751): the seeding revision a new world is built at (`world.seedRevision`, saved; a load replays the
     * save's, absent = 0). An anchor entry whose `since` is above a world's revision is skipped, so a seeded station
     * added later never shifts an older world's stations. A `since` above `revision` fails the data check (data.ts).
     */
    revision: z.number().int().nonnegative().default(0),
    rimEmpty: z.boolean(),
    anchorMode: z.enum(['add', 'replace']),
    startFill: unitRange,
    builtFill: unit,
    levelTwoChance: unit,
    naming: z.strictObject({ format: idString, holdfastFormat: idString, holdfastFaction: factionIdSchema }),
    fields: z.strictObject({
      reservePerKm: positive,
      regrowPerDay: unit,
      regrowOverride: z.partialRecord(wareIdSchema, unit),
      /** MC-e7b3: per-ore × on `reservePerKm` (absent = 1): the strategic tonnes of a scarce ore, never its rock share. */
      reserveMult: z.partialRecord(wareIdSchema, unit).optional(),
    }),
    placement: z.strictObject({
      minSeparationM: positive,
      retries: posInt,
      fieldOffsetM: numRange,
      bodyRadii: numRange,
      bodyMinM: positive,
      relayInwardM: numRange,
      gateOffsetM: positive,
      freeRadiusM: numRange,
      rules: z.record(idString, placementRuleSchema),
    }),
    homeOffsets: z.record(z.enum(M2_STATION_IDS), vec3),
    archetypes: z.record(z.enum(ARCHETYPES), z.array(archetypeEntrySchema)),
    anchors: z.record(z.enum(ANCHOR_IDS), z.array(anchorEntrySchema)),
    owners: z.record(idString, regionWeightsSchema),
    buildPlans: z.strictObject({
      perFaction: intRange,
      startDay: intRange,
      templates: z.partialRecord(factionIdSchema, z.array(idString).min(1)),
    }),
    /**
     * M10 P4a2c: the level-2 seed guarantee, append-only (`since` non-decreasing, each ≤ `revision`, D-751's rule for
     * anchor `since`). Shipped (P4a7b, D-857): `{ since: 2, makers: ['meridian'] }` at `revision` 2. Every
     * maker must be a hull-lines maker (checked at seeding, `economy/seed.ts applyLevelTwoGuarantee`).
     */
    levelTwoGuarantee: z.array(levelTwoGuaranteeEntrySchema).default([]),
  })
  .superRefine((f, ctx) => {
    let prev = 0
    for (const [i, e] of f.levelTwoGuarantee.entries()) {
      if (e.since > f.revision) ctx.addIssue({ code: 'custom', path: ['levelTwoGuarantee', i, 'since'], message: `since ${e.since} is above revision ${f.revision}` })
      if (e.since < prev) ctx.addIssue({ code: 'custom', path: ['levelTwoGuarantee', i, 'since'], message: `since ${e.since} is below the entry before it (${prev}); the list is append-only` })
      prev = e.since
    }
  })
export type EconomySeedFile = z.infer<typeof economySeedFileSchema>

// ---------------------------------------------------------------------------------------------
// hulls.json (spec §4.6)
// ---------------------------------------------------------------------------------------------

/** The wares a hull `buildCost` may name: the component tier and the refractory alloy (U7, MC-e3, T33). */
export const HULL_COST_WARE_IDS = [...COMPONENT_WARE_IDS, 'refractory_alloy'] as const

/**
 * M10 P2d1: the module slot ids a hull `fit` may name. The worker can't import the main-thread data schemas, so this
 * copies `src/data/m2Schemas.ts SLOT_IDS` (`cargoClass.test.ts` checks they are equal).
 */
export const HULL_FIT_SLOT_IDS = [
  'weapon1',
  'weapon2',
  'miningLaser',
  'salvageBeam',
  'cargo',
  'shield',
  'power',
  'thrusters',
  'scanner',
] as const
/** M10 P5a6b H4: the least hold multiplier a hull `fit` may give a cargo class (1×: a class never takes more hold). */
export const HULL_HOLD_MULT_MIN = 1
/** The turret mounts a hull `fit` may carry: `src/data/schemas.ts MAX_TURRET_MOUNTS` (checked by `cargoClass.test.ts`). */
export const HULL_FIT_MAX_TURRETS = 4
/** The module marks a hull `stockFit` may name (`src/data/m2Schemas.ts MODULE_MKS`). */
const HULL_STOCK_MK_MIN = 1
const HULL_STOCK_MK_MAX = 3

/**
 * M10 P2d1 (spec §7 P2d): a hull's fitting envelope. Read by nobody until P5a4; `hulls.json` carries none yet.
 * `slots`: module slots by slot id; `holdT`/`powerT`/`shieldT`: hold tonnes, power and shield budgets; `holdMult`: a
 * per-cargo-class hold multiplier; `turrets`: turret mounts; `cradle`: a docking cradle (0 or 1).
 */
export const hullFitSchema = z.strictObject({
  slots: z.partialRecord(z.enum(HULL_FIT_SLOT_IDS), nonNegInt),
  holdT: nonNegative,
  powerT: nonNegative,
  shieldT: nonNegative,
  // M10 P5a6b H4: a multiplier is at least 1 (0 read as 1 and values below 1 over-filled the raw callers).
  holdMult: z.partialRecord(cargoClassSchema, z.number().min(HULL_HOLD_MULT_MIN)),
  turrets: nonNegInt.max(HULL_FIT_MAX_TURRETS),
  cradle: nonNegInt.max(1),
})
export type HullFit = z.infer<typeof hullFitSchema>

/** M10 P2d1: the fit a hull ships with (`mk`: the module mark; P5a5 extends it). Read by nobody until P5a5. */
export const hullStockFitSchema = z.strictObject({
  mk: z.number().int().min(HULL_STOCK_MK_MIN).max(HULL_STOCK_MK_MAX),
})
export type HullStockFit = z.infer<typeof hullStockFitSchema>

export const hullSchema = z.strictObject({
  id: idString,
  shipDef: idString,
  patrolShipDef: idString.nullable(),
  /** The AI jobs that buy this hull. M10 P4b1a1: may be empty (a survey or tug row, a variant with `roles: []`). */
  roles: z.array(roleIdSchema),
  escort: z.boolean(),
  cargo: positive,
  strength: positive,
  toughness: positive,
  /**
   * Component wares plus the refined alloy (spec §4.6 as changed by U7, MC-e3, T33): any other key is rejected. The alloy
   * is tier refined, so `COMPONENT_WARES` (the LR and SINK metrics) and the hulk kit and scrap (the claim cost, T52)
   * ignore it.
   */
  buildCost: z
    .partialRecord(z.enum(HULL_COST_WARE_IDS), positive)
    .refine((c) => Object.keys(c).length > 0, { message: 'a hull needs a build cost' }),
  buildSeconds: positive,
  wreckLayout: idString,
  // M6 (plan P0; P5 fills them in hulls.json): the player-buyable hulls.
  /** The M6 ship class this hull sells as (several hulls may share a class, M10 P2b1); absent = not sold to the player. */
  class: z.enum(SHIP_CLASS_NAMES).optional(),
  /** Crew (M6 spec §4). */
  crew: posInt.optional(),
  /** The player's standing with the yard's faction needed to buy it (−100..100). */
  standingReq: z.number().min(-100).max(100).optional(),
  /** M10 P2d1: the fitting envelope (optional; read from P5a4). */
  fit: hullFitSchema.optional(),
  /** M10 P2d1: the stock fit (optional; read from P5a5). */
  stockFit: hullStockFitSchema.optional(),
  // M10 P2a2 (spec §6.1): stamped by the build-time compile (`tools/hulls/compile.mjs`) from `hull-lines.json`; never
  // hand-edited. Read by nobody in the sim until P2d2.
  /** The size band (`bands.json` ids). */
  band: z.enum(HULL_BAND_IDS).optional(),
  /** The maker line id (`<maker>.<role>`); absent on an unlined hull. */
  line: idString.optional(),
  /** The maker faction; absent on an unlined hull. */
  maker: factionIdSchema.optional(),
  /** The Charter toll class (legacy rows: `claims.json toll.hullClass`, default medium; new rows: the band's). */
  tollClass: z.enum(HULL_CLASS_NAMES).optional(),
  /** The wing cap a ship of this hull leads (absent = `fleet.json wing.size`). */
  wingSize: posInt.optional(),
  export: z.boolean().optional(),
  story: z.boolean().optional(),
  unique: z.boolean().optional(),
  relic: z.boolean().optional(),
  /** No longer built; the row stays (rows are never deleted, the hull index is saved raw). */
  retired: z.boolean().optional(),
  /**
   * M10 P4a6 (plan 2026-10-11-m10-p4a §0.3): stamped `false` from a `hull-lines` line hull's `sale` (the cutter, the
   * courier): never offered in a shipyard and refused by `buyShipAt`. Absent = sold (when the hull has a class).
   */
  sale: z.boolean().optional(),
  /** M10 P4b1a1: stamped from the line hull's `lengthM` (the art target, m); read by P5's readouts. */
  lengthM: positive.optional(),
})
export type HullDef = z.infer<typeof hullSchema>

export const hullsFileSchema = z
  .strictObject({
    comment,
    priceMargin: positive,
    /**
     * Balance pass T4 (shipyards, plan §2.5): the yard rules. Every default is the M4/M5 behaviour (off).
     * - `payOnDelivery` (D-314 D1): an AI order is paid when the ship is delivered, not when it is placed. Player
     *   orders stay prepaid.
     * - `skipAhead`: a queued order whose components are missing no longer holds the orders behind it, unless it
     *   has waited `skipAheadMaxWaitDays` or more (then the queue waits for it, so stock builds up for it).
     * - `buildTimeMult`: × every hull’s `buildSeconds` (yard throughput).
     * - `ownerFirst`: the yard owner's own orders (free at its own yard) start before other factions' orders, so the
     *   owner's fleet isn't starved behind everyone's queue.
     */
    yard: z
      .strictObject({
        payOnDelivery: z.boolean().default(false),
        skipAhead: z.boolean().default(false),
        skipAheadMaxWaitDays: positive.default(5),
        buildTimeMult: positive.default(1),
        ownerFirst: z.boolean().default(false),
      })
      .default({ payOnDelivery: false, skipAhead: false, skipAheadMaxWaitDays: 5, buildTimeMult: 1, ownerFirst: false }),
    /**
     * M8 SNK-0c (D-TBD): the faction ship-order queue fix, found by the SNK-0 sizing run (orders waited on yard
     * slots at one yard per seed; an M6 g1 day-0 order never started in 200 days). Off by default, so every golden
     * and `M4_AI_HASH` are unchanged until gate A (SNK-7 turns it on with the sinks). With `enabled`:
     * - **Queue-aware yard choice** (`sim.ts orderYard`): within each preference tier the yard with the lowest
     *   `hops + loadHops × open orders ÷ slots` wins (ties: the lower index), and `any` (or a preference with no
     *   reachable yard) means anyone's yard, not Varro's first. Raider orders score the same way, the preferred
     *   builder's yards within `replenishRange` of the base first.
     * - **Aged orders first** (`shipyard.ts stepShipyards`): a queued order that has waited `ownerFirstMaxAgeDays`
     *   faction days or more starts before the owner's `ownerFirst` orders, in queue order.
     * - **Open-order cap** (`sim.ts fillJobs`): a faction holding `maxOpenOrdersPerFaction` open orders places no
     *   more (0 = no cap; with `jobs.json maxOpenOrdersPerFaction` also set, the lower wins).
     * - **Hold cap** (SUP-1b, D-743; `shipyard.ts stepShipyards`): an aged order missing components holds its yard for at
     *   most `holdMaxDays` (0 = no cap); after that it stops holding (later orders start past it) but still starts first
     *   whenever its bill is in stock.
     * - **Aged bill withheld from sale** (M10 P4a7e, D-TBD; `stock.ts yardHeldBill`): with `withholdAgedBill` (default on;
     *   inert while `enabled` is off) a yard keeps its aged orders' bill back from every sale and take (the source table,
     *   trader lots, `sellable`), so its trading post can't sell the components the held order waits on.
     * No runtime state: queue loads and ages derive from the saved order book.
     */
    yardQueue: z
      .strictObject({
        enabled: z.boolean().default(false),
        loadHops: nonNegative.default(1),
        ownerFirstMaxAgeDays: positive.default(20),
        maxOpenOrdersPerFaction: nonNegInt.default(0),
        holdMaxDays: nonNegative.default(0),
        withholdAgedBill: z.boolean().default(true),
      })
      .default({ enabled: false, loadHops: 1, ownerFirstMaxAgeDays: 20, maxOpenOrdersPerFaction: 0, holdMaxDays: 0, withholdAgedBill: true }),
    hulls: z.array(hullSchema).min(1),
  })
  .superRefine((file, ctx) => {
    const ids = new Set<string>()
    for (const h of file.hulls) {
      if (ids.has(h.id)) ctx.addIssue({ code: 'custom', message: `duplicate hull id ${h.id}` })
      ids.add(h.id)
    }
  })
export type HullsFile = z.infer<typeof hullsFileSchema>

// ---------------------------------------------------------------------------------------------
// roles.json and jobs.json (spec §4.8, §7.5, §7.6)
// ---------------------------------------------------------------------------------------------

export const rolesFileSchema = z.strictObject({
  comment,
  trader: z.strictObject({
    jumpRange: nonNegInt,
    minMargin: nonNegative,
    riskWeight: nonNegative,
    maxRisk: unit,
    minLot: positive,
    reservationGraceS: nonNegative,
    idleRetryS: positive,
    owners: factionWeights,
    hulls: idWeights,
  }),
  miner: z.strictObject({
    homeRange: nonNegInt,
    sellRange: nonNegInt,
    mineRate: positive,
    availabilityRef: positive,
    availabilityMin: unit,
    riskWeight: nonNegative,
    idleRetryS: positive,
    rigWares: z.array(wareIdSchema),
    /**
     * Balance pass T3 (D5, sell floor): an AI miner skips an ore whose best sink's projected fill
     * ((stock + incoming) ÷ capacity) is above this. 1 = off (the default for an untuned merge).
     */
    maxSinkFill: unit.default(1),
    /**
     * Round 2 G1 (completes D-314 D5): when true, the `maxSinkFill` floor also applies wherever an AI miner commits
     * ore to a sink (the sale planned at the decision and after mining, the divert from a full sink, the waiting
     * retry), not only when choosing what to mine. Player ships keep their sell policy. false = off (the default).
     */
    saleFloor: z.boolean().default(false),
    /**
     * Round 2 G1: wares never floored at sale. Tetherstone counts into the gate's extraction when mined, so a miner
     * holding unsold tetherstone would throttle the gate law; the default keeps it exempt.
     */
    saleFloorExempt: z.array(wareIdSchema).default(['tetherstone']),
    /**
     * Balance pass T3 (tetherstone flow): the sell range, in jumps, for `rigWares` lots (the sink search when
     * deciding, and the sale after mining or while waiting). Absent = `sellRange` (off). At most 3 (the widest
     * neighbour list).
     */
    rigSellRange: z.number().int().min(0).max(3).optional(),
    /**
     * Refractory reach (D-TBD, card RR-1a): an AI, non-rig miner whose home buys a dormant ware (a recipe's
     * `dormantOn`, the refractory ore) and is short of it also scans fields this many jumps from home for that ore
     * only. Absent = off (identical to before); 0 or 1 (≤ `homeRange`) add no ring. At most 3 (the widest neighbour
     * list) and at most `sellRange` (the home stays a sink in range).
     */
    dormantFetchRange: z.number().int().min(0).max(3).optional(),
    /**
     * RR-F1 (card F1a1, round-2 option B): when true, an AI, non-rig miner on a floored ore (`aiSaleFill` < 1) books
     * its sale when it decides to mine: the lot is sized to the room under `bookedLotFill` at the decision's sink
     * and committed there as incoming as soon as the ore is reserved, so the next decider sees that sink as filling.
     * Rig miners, exempt wares (tetherstone) and player ships keep the old path. false = off (identical to before).
     */
    commitAtDecision: z.boolean().default(false),
    /**
     * RR-F1 (D-803 C1): the fill a booked lot may bring its sink to, as a share of capacity. Absent = `maxSinkFill`
     * (the floor, so a booking never fills a sink past it and the top band stays for the pilot); 1 = the probe-only
     * "cap" arm. Read only when `commitAtDecision` is on. At least `maxSinkFill`.
     */
    bookedLotFill: z.number().positive().max(1).optional(),
    /**
     * RR-F1 (card F1a3, D-803 C2): AI non-rig miners only; the player fleet reads `mineRate` and `mineRateMult`;
     * absent = 1. An AI miner without the `fractureRig` flag mines at `mineRate × availability × aiMineRateMult`
     * (its scored mining time and its stay at the field are divided by it), so a slower rate keeps more ships
     * visibly mining for the same ore. In (0, 1].
     */
    aiMineRateMult: z.number().positive().max(1).optional(),
    /**
     * RR-F1 (card F1b, perf S1's back-off): when set, an AI, non-rig miner with an empty hold that finds nothing to
     * mine waits `idleEmptyRetryS × (0.5 + u)` seconds (`u` in [0, 1) from its seed, fixed per agent) instead of
     * `idleRetryS` before it decides again. Waiting-with-cargo retries, rig miners and player ships keep
     * `idleRetryS`. Absent = off (identical to before). At least `idleRetryS`.
     */
    idleEmptyRetryS: positive.optional(),
    /**
     * RR-F1 (card F1e1, Q5 (c), D-803): when set to N, an AI, non-rig miner parked with a floored ore lot
     * (`aiSaleFill` < 1) whose retried sale fails returns the lot to its field's reserve on its window day
     * (`(day + phase) mod N = 0`, the phase from its seed, fixed per agent), once that day, when it waits at a
     * field node holding the ore with room for the whole lot (`initial − reserve` ≥ cargo; never clamped). The ore
     * stays in the world (D-360 D3 ruling, D-803). Absent = off (identical to before).
     */
    returnLotWindowDays: z.number().int().positive().optional(),
    hulls: idWeights,
  }).refine((m) => m.dormantFetchRange === undefined || m.dormantFetchRange <= m.sellRange, {
    message: 'miner.dormantFetchRange must be at most sellRange',
  }).refine((m) => m.bookedLotFill === undefined || m.bookedLotFill >= m.maxSinkFill, {
    message: 'miner.bookedLotFill must be at least maxSinkFill',
  }).refine((m) => m.idleEmptyRetryS === undefined || m.idleEmptyRetryS >= m.idleRetryS, {
    message: 'miner.idleEmptyRetryS must be at least idleRetryS',
  }),
  supplier: z.strictObject({
    jumpRange: nonNegInt,
    restockMargin: unit,
    minDeficit: unit,
    idleRetryS: positive,
    /**
     * Balance pass T3 (tetherstone flow): an AI supplier ranks an export terminal's restock deficit at this many
     * times its tonnes, so the small terminal is served before large deficits elsewhere. 1 = off (the default).
     */
    exportDeficitWeight: positive.default(1),
    /**
     * 5(e) (MC-e7b4, D-TBD): per ware, an AI supplier's priority for a yard whose queued order waits on that ware (stock +
     * incoming under the order's cost): the restock is due whatever the margin rules say, its target is max(targetFill ×
     * capacity, that cost) (a level-1 yard keeps 8 t of alloy at 0.4; a corvette costs 12), and the deficit ranks at
     * this many times its tonnes. The refractory alloy's yard store (20 t a level, 2-12 t a hull) is never the largest
     * deficit by tonnes, so without it an order waits on 2 t while suppliers fill 60 t plating gaps. Absent = none.
     */
    heldOrderWares: z.partialRecord(wareIdSchema, positive).default({}),
    /**
     * SUP-1 (D-743): an AI supplier tries this many of its best restock deficits (held-first list, then the plain list), in
     * score order, and takes the first with a source. 1 = the pre-SUP-1 single pick. At most 8 (the scratch length).
     */
    deficitTries: posInt.max(8).default(1),
    /**
     * SUP-2a (D-751): an aged queued order at a yard (SNK-0c's aged pass holds the yard for it) may draw its yard owner's
     * own maker stations of the ware (a line outputs it) down to this fill, the lot capped at the aged order's shortfall
     * (its cost minus the yard's stock − reserved + incoming). AI held-list candidates only. Absent = off.
     */
    agedSourceFill: unit.optional(),
    hulls: idWeights,
  }),
  patrol: z.strictObject({ respondRange: nonNegInt, reevaluateS: positive, responseStayS: positive, hulls: idWeights }),
  convoy: z
    .strictObject({
      importIntervalS: positive,
      importHaulers: posInt,
      importEscorts: nonNegInt,
      importLots: wareQty,
      /**
       * 5(e) MC-e4 (RD6): the import with a price floor and a skip roll (the refractory alloy). Absent = none. The
       * Export Terminal sells it at ≥ `importPriceFloorMult` × the deep median; its lot is skipped with
       * `importSkipChance` per import interval.
       */
      importFloorWare: wareIdSchema.optional(),
      importPriceFloorMult: positive.default(1),
      importSkipChance: unit.default(0),
      exportLot: positive,
      exportIntervalS: positive,
      exportMinLot: positive,
      exportPriceMult: nonNegative,
      importCostMult: nonNegative,
      hulls: idWeights,
      escortHulls: idWeights,
    })
    .refine((c) => c.importFloorWare === undefined || (c.importLots[c.importFloorWare] ?? 0) > 0, {
      message: 'importFloorWare must have an import lot',
    }),
  raider: z.strictObject({
    groupSize: intRange.refine(([lo]) => lo >= 1, { message: 'a group has at least one ship' }),
    sortieS: numRange,
    lurkS: positive,
    targetRange: nonNegInt,
    interceptChance: unit,
    lootFraction: unit,
    returnCargoFill: unit,
    returnHp: unit,
    replenishRange: nonNegInt,
    hulls: idWeights,
  }),
  /**
   * SUP-1 (D-743): `wareTries`: a constructor tries this many of a site's wares (largest net need first) for one that
   * has a source. 1 = the pre-SUP-1 single pick. At most 8.
   */
  constructor: z.strictObject({ perSite: posInt, wareTries: posInt.max(8).default(1), hulls: idWeights }),
})
export type RolesFile = z.infer<typeof rolesFileSchema>

export const jobsFileSchema = z.strictObject({
  comment,
  minersPerRefineryLevel: nonNegative,
  veinRigMinersPerRefineryLevel: nonNegative,
  minersPerIceWorksLevel: nonNegative,
  tethysTraceMinersPerHomeTraceField: nonNegative,
  mutualFractureRigShare: unit,
  noFractureRig: z.array(factionIdSchema),
  suppliersPerStation: nonNegative,
  minSuppliersPerSystem: nonNegInt,
  tradersPerSystem: nonNegative,
  authorityPatrolsPerGateSystem: nonNegative,
  authorityPatrolsPerStationSystem: nonNegative,
  factionPatrolsPerSystem: nonNegative,
  factionPatrolMinStations: nonNegInt,
  varroPatrolsPerYardSystem: nonNegative,
  /**
   * D-949 J1 (D-1145): when true, `jobHome`'s patrol target per home comes from `agents/jobs.ts visitJobs` (the M5
   * hook's `patrolMult`, the `expand` `patrolExtra` and the Tethys escorts) instead of the static per-system rule.
   * With an identity hook the two agree. False = off (the static rule).
   */
  patrolHomeFromJobs: z.boolean().default(false),
  /**
   * D-949 J2 (D-1145): when true, `jobHome` skips a cut-off home (no yard reachable from its system,
   * `economy/shipyard.ts yardReachable`, D-950's definition), so the next-largest gap that can order wins; all cut off
   * → −1 (`fillJobs` blocks the role as before). False = off (the largest gap wins, reachable or not).
   */
  jobHomeSkipCutOff: z.boolean().default(false),
  raidersPerBase: nonNegative,
  exportHaulersPerTerminal: nonNegative,
  maxOrdersPerFactionDay: nonNegInt,
  /**
   * Balance pass T4 (plan §2.5, T3 root cause): when true, a faction short of fracture-rig miners at a home (its job
   * rule rig count − live rigged miners − open rig orders) orders one rig miner there each faction day before its
   * other deficits; the order is built rigged and joins its yard queue ahead of the other queued orders. False = off.
   */
  rigReplacePriority: z.boolean().default(false),
  /**
   * Balance pass T4 (plan §2.5): the most open (queued or building) shipyard orders a faction may hold; job fill stops
   * ordering at the cap. Open orders count as filled jobs and against strategic.json `maxAgents`, so without a cap
   * the early factions' queues fill the population cap and the later factions (Meridian, Varro) can't order. 0 = off.
   */
  maxOpenOrdersPerFaction: nonNegInt.default(0),
  /**
   * Balance pass T4 part 3: when true, job fill visits the factions starting at `day mod factionCount` instead of
   * always at faction 0, so no faction is always last in line for the `maxAgents` room. False = off (index order).
   */
  rotateFactionOrder: z.boolean().default(false),
  /**
   * Balance pass T4 part 3: a per-faction open-order cap scaled to the faction's own job targets: job fill stops at
   * `ceil(share × Σ targets)` open orders (with `maxOpenOrdersPerFaction`, the lower wins). 0 = off.
   */
  maxOpenOrdersTargetShare: z.number().min(0).max(1).default(0),
  /**
   * Balance pass T4 part 3, growth yields to replacement: while a faction holds `growthYieldOpenOrders` or more open
   * orders, it orders only for roles whose live ships are below `growthYieldBelow` × target (roles that lost ships),
   * so a long queue stops turning into fleet growth. 0 = off.
   */
  growthYieldOpenOrders: nonNegInt.default(0),
  growthYieldBelow: z.number().min(0).max(1).default(0.9),
  /**
   * Balance pass T7b: when true, growth yield reads a real per-faction loss counter (`factionAi.unreplacedLosses`,
   * raised per ship lost in `agentLost`, lowered per order placed) instead of live vs target: while the faction holds
   * `growthYieldOpenOrders` or more open orders, it orders only while it has an unreplaced loss. False = the live-vs-target proxy.
   */
  growthYieldLosses: z.boolean().default(false),
  /**
   * Balance pass T7b part 2, target ramp: a faction's job targets (Σ over roles) are capped at a per-faction ramp
   * (`factionAi.targetCap`), updated once per faction day by job fill: cap = min(Σ rule targets, max(cap +
   * `targetRampPerDay`, live ships × (1 + `targetRampLead`))). A station-driven target jump then ramps in as the
   * yards deliver instead of queueing hundreds of orders at once; a drop in the rule targets applies at once, and the
   * cap never falls with losses (a lost ship is still re-ordered). Roles are scaled in proportion. Both 0 = off.
   */
  targetRampPerDay: nonNegative.default(0),
  targetRampLead: nonNegative.default(0),
  firstDecisionS: numRange,
})
export type JobsFile = z.infer<typeof jobsFileSchema>

// ---------------------------------------------------------------------------------------------
// factions.json (spec §4.7)
// ---------------------------------------------------------------------------------------------

export const FACTION_KINDS = ['player', 'outlaw', 'charter', 'megacorp', 'independent', 'descendant'] as const
export const factionSchema = z.strictObject({
  id: factionIdSchema,
  name: idString,
  /** M1 `Team.id` (0 is the player). */
  teamId: z.number().int().min(1).max(15),
  kind: z.enum(FACTION_KINDS),
  treasury: z.number(),
  livery: idString,
  yard: z.union([z.literal('own'), z.literal('any'), factionIdSchema]),
  active: z.boolean(),
  // M5 (M5 spec §4.1)
  /** Outlaws never claim, pay or receive Charter money, or vote (must match kind 'outlaw'). */
  outlaw: z.boolean(),
  /** The faction it answers to (the guardian cell → holdfast), null = none. */
  parent: factionIdSchema.nullable(),
  /** Hidden escalation bias (Varro 0.9). */
  escalationBias: unit.optional(),
  /** Personality weights 0..1, every trait. */
  personality: z.strictObject(Object.fromEntries(TRAIT_NAMES.map((t) => [t, unit])) as Record<(typeof TRAIT_NAMES)[number], typeof unit>),
})
export type FactionDef = z.infer<typeof factionSchema>

export const factionsFileSchema = z.strictObject({
  comment,
  /**
   * Balance pass T5: the share of its starting treasury a faction keeps in reserve when placing a pay-on-delivery
   * ship order (`placeOrder` needs treasury − open dues − orderReserve × start ≥ price). 0 = off.
   */
  orderReserve: unit.default(0),
  factions: z.array(factionSchema).superRefine((factions, ctx) => {
    const ids = factions.map((f) => f.id)
    if (ids.length !== FACTION_IDS.length || ids.some((id, i) => id !== FACTION_IDS[i])) {
      ctx.addIssue({ code: 'custom', message: `factions must be exactly [${FACTION_IDS.join(', ')}] in this order` })
    }
    const teams = new Set(factions.map((f) => f.teamId))
    if (teams.size !== factions.length) ctx.addIssue({ code: 'custom', message: 'faction teamIds must be unique' })
    for (const f of factions) {
      if (f.outlaw !== (f.kind === 'outlaw')) ctx.addIssue({ code: 'custom', message: `faction "${f.id}": outlaw must match kind 'outlaw'` })
      if (f.parent === f.id) ctx.addIssue({ code: 'custom', message: `faction "${f.id}" cannot be its own parent` })
    }
  }),
  /** M5 starting faction-to-faction standings (M5 spec §4.1): symmetric pairs; `default` for the rest. */
  standings: z.strictObject({
    default: z.number().min(-100).max(100),
    pairs: z
      .array(
        z
          .strictObject({ a: factionIdSchema, b: factionIdSchema, value: z.number().min(-100).max(100), hidden: z.boolean().optional() })
          .refine((p) => p.a !== p.b, { message: 'a standing pair needs two factions' }),
      )
      .superRefine((pairs, ctx) => {
        const seen = new Set<string>()
        for (const p of pairs) {
          const key = p.a < p.b ? `${p.a}|${p.b}` : `${p.b}|${p.a}`
          if (seen.has(key)) ctx.addIssue({ code: 'custom', message: `duplicate standing pair ${key}` })
          seen.add(key)
        }
      }),
  }),
})
export type FactionsFile = z.infer<typeof factionsFileSchema>

// ---------------------------------------------------------------------------------------------
// strategic.json, travel.json, combat.json, gate.json (spec §3.5, §4.9, §7.3, §8, §9)
// ---------------------------------------------------------------------------------------------

export const strategicConfigSchema = z
  .strictObject({
    comment,
    clock: z.strictObject({
      fineS: positive,
      slowS: positive,
      dayS: positive,
      combatRoundS: positive,
      opportunityRefreshS: positive,
      coarseSubsteps: posInt,
      daySlices: posInt,
      advanceIntervalS: positive,
      warpMax: positive,
    }),
    caps: z.strictObject({
      maxAgents: posInt,
      poolCapacity: posInt,
      maxHops: posInt.max(255),
      maxStations: posInt.max(65535),
      linesPerStation: posInt.max(255),
      maxNodes: posInt,
      maxFields: posInt,
      maxGroups: posInt,
      groupMembers: posInt.max(255),
      maxOrders: posInt,
      maxWrecks: posInt,
      maxSites: posInt,
      maxEncounters: posInt,
      encounterSide: posInt.max(255),
      factionCapacity: posInt.max(254),
      heapCapacity: posInt,
      eventRing: posInt,
      notableRing: posInt,
      /** M6 (spec §9): the player's ship and station caps. */
      playerShips: posInt,
      playerStations: posInt,
    }),
    /** M7 (plan S16): the story store's capacities and the Tethys income window (faction days). */
    story: z.strictObject({
      grievances: posInt.max(255),
      restored: posInt,
      titles: posInt,
      challenges: posInt,
      ships: posInt,
      incomeDays: posInt,
    }),
    catchUp: z.strictObject({ capS: positive, hiddenResumeS: positive }),
    rollupDays: posInt,
    wireKeep: posInt,
    wireSaved: posInt,
    historyDays: posInt,
  })
  .superRefine((c, ctx) => {
    const k = c.caps
    if (k.maxAgents > k.poolCapacity) ctx.addIssue({ code: 'custom', message: 'caps.maxAgents must not exceed caps.poolCapacity' })
    if (k.heapCapacity < k.poolCapacity) ctx.addIssue({ code: 'custom', message: 'caps.heapCapacity must be at least caps.poolCapacity' })
    for (const key of ['eventRing', 'notableRing'] as const) {
      if (!isPowerOfTwo(k[key])) ctx.addIssue({ code: 'custom', message: `caps.${key} must be a power of two` })
    }
    if (c.clock.slowS % c.clock.fineS !== 0 || c.clock.dayS % c.clock.slowS !== 0) {
      ctx.addIssue({ code: 'custom', message: 'clock: slowS must be a multiple of fineS and dayS a multiple of slowS' })
    }
    if (c.clock.dayS % c.clock.coarseSubsteps !== 0) ctx.addIssue({ code: 'custom', message: 'clock: dayS must split into coarseSubsteps' })
    if (c.wireSaved > c.wireKeep) ctx.addIssue({ code: 'custom', message: 'wireSaved must not exceed wireKeep' })
  })
export type StrategicConfig = z.infer<typeof strategicConfigSchema>

export const travelConfigSchema = z
  .strictObject({
    comment,
    k: positive,
    dropM: positive,
    tauS: nonNegative,
    vMin: positive,
    vMax: positive,
    sublightBelowM: positive,
    sublightMinS: nonNegative,
    sublightSpeed: positive,
    undockS: nonNegative,
    approachS: nonNegative,
    dockS: nonNegative,
    relayTransitS: nonNegative,
  })
  .refine((t) => t.vMin < t.vMax, { message: 'vMin must be below vMax' })
export type TravelConfig = z.infer<typeof travelConfigSchema>

export const combatConfigSchema = z.strictObject({
  comment,
  roundS: positive,
  maxRounds: posInt,
  damageScale: positive,
  damageJitter: numRange,
  fleeChance: unit,
  /** AI suppliers' per-round flee chance (SF-1); absent or 0 = they never roll (no draw). */
  supplierFleeChance: unit.optional(),
  fleeDelayS: nonNegative,
  raiderDisengageHp: unit,
  patrolJoinChance: unit,
  patrolMeetChance: unit,
  repairS: positive,
  wreckOffsetM: nonNegative,
  wreckLifetimeDays: positive,
  danger: z.strictObject({ lossBumpMax: unit, lossValueScale: positive, decay: unit, perRaiderGroup: unit }),
})
export type CombatConfig = z.infer<typeof combatConfigSchema>

export const gateConfigSchema = z
  .strictObject({
    comment,
    start: nonNegative,
    min: nonNegative,
    max: positive,
    kPerTonne: nonNegative,
    regrowthPerDay: nonNegative,
    regrowthBelowTonnes: nonNegative,
    /**
     * Balance pass T2 (§2.7). Every key defaults to "off" (the M4 law unchanged): `smoothDays` 1 feeds the raw day
     * to the law instead of an extraction EMA; `recoveryPerDay` 0 adds no proportional recovery toward `max`;
     * `storyExtractionMult` 1 is the data base of the reactive-clock hook (M7 spec §10.1: Tethys extracts faster as
     * the story tier rises); it scales **AI** extraction only, times the saved runtime `state.gate.storyMult`, and the
     * product is capped at `storyMultCap` (§10.3; D-314 D2: AI mining alone never closes the gate);
     * `catchUpClamp` false lets an offline absence move the gate freely (D-314 D3). `aiFloor` (M7 T20, D-TBD the
     * gate's AI floor) keeps the law from taking an open gate below `closedAt + 1 − kPerTonne × playerEma`: AI and
     * story extraction alone never close it; the player's (and the player corporation's) own mining can. False = off.
     */
    smoothDays: z.number().min(1).default(1),
    recoveryPerDay: unit.default(0),
    storyExtractionMult: nonNegative.default(1),
    storyMultCap: positive.default(2.6),
    catchUpClamp: z.boolean().default(false),
    catchUpMinStability: nonNegative.default(1),
    aiFloor: z.boolean().default(false),
    /** M5 gate stages (M5 spec §10; C3). */
    stages: z
      .strictObject({
        flickersBelow: nonNegative,
        tremorsBelow: nonNegative,
        closedAt: nonNegative,
        flicker: z.strictObject({ convoyWaitS: numRange, importMult: unit, chatterChance: unit }),
        tremor: z.strictObject({ closureChance: unit, closureS: numRange, authorityGatePatrolMult: positive, wireSeverity: z.number().int().min(1).max(3) }),
        isolation: z.strictObject({
          authoritySubsidyMult: unit,
          authorityPatrolMult: nonNegative,
          holdfastExpansionAdd: nonNegative,
          holdfastInfluencePerDay: nonNegative,
        }),
        reopenAbove: nonNegative,
        reopenDays: posInt,
        recoveringDays: nonNegInt,
        visibleTier: z.number().int().min(0).max(5),
      })
      .refine((s) => s.closedAt < s.tremorsBelow && s.tremorsBelow < s.flickersBelow, { message: 'gate stages: closedAt < tremorsBelow < flickersBelow' }),
  })
  .refine((g) => g.min <= g.start && g.start <= g.max, { message: 'gate: min ≤ start ≤ max' })
  .refine((g) => g.catchUpMinStability > g.stages.closedAt, { message: 'gate: catchUpMinStability > stages.closedAt (an absence never closes the gate)' })
export type GateConfig = z.infer<typeof gateConfigSchema>

// ---------------------------------------------------------------------------------------------
// wire.json (spec §12)
// ---------------------------------------------------------------------------------------------

export const WIRE_TEMPLATE_KEYS = [
  'shortage',
  'surplus',
  'priceUp',
  'priceDown',
  'stationBuilt',
  'shipsBuilt',
  'fieldDepleted',
  'convoyLost',
  'raid',
] as const
export type WireTemplateKey = (typeof WIRE_TEMPLATE_KEYS)[number]
export const WIRE_SLOTS = ['station', 'system', 'ware', 'faction', 'cause'] as const
export const PRICE_CAUSE_TAGS = [
  'supplyShock',
  'convoyLost',
  'raid',
  'inputShortage',
  'demand',
  'dump',
  'oversupply',
] as const satisfies readonly PriceCauseTag[]

/** Every `{slot}` in `text` is one of `slots`. */
function slotsOk(text: string, slots: readonly string[]): boolean {
  for (const m of text.matchAll(/\{([^}]*)\}/g)) if (!slots.includes(m[1])) return false
  return true
}
const templateText = (slots: readonly string[]): z.ZodType<string> =>
  z.string().min(1).refine((t) => slotsOk(t, slots), { message: `unknown {slot}; allowed: ${slots.join(', ')}` })

const WIRE_SEVERITY = z.union([z.literal(1), z.literal(2), z.literal(3)])
/** Wire kinds the generator makes (every kind but the injected `notice`). */
export const WIRE_SEVERITY_KINDS = [
  'shortage',
  'surplus',
  'priceShock',
  'stationBuilt',
  'shipsBuilt',
  'fieldDepleted',
  'convoyLost',
  'raid',
] as const satisfies readonly WireKind[]
export const wireConfigSchema = z.strictObject({
  comment,
  shortageFill: unit,
  surplusFill: unit,
  priceShock: positive,
  maxPerDay: nonNegInt,
  localExtra: nonNegInt,
  localJumps: nonNegInt,
  mergeDays: nonNegative,
  causeDays: positive,
  dumpShare: unit,
  tickerS: positive,
  tickerQueue: posInt,
  causeScores: z.strictObject({ lostCarrier: unit, upstreamStarved: unit, raidNearby: unit, demandJump: unit, playerDump: unit }),
  severity: z.record(z.enum(WIRE_SEVERITY_KINDS), WIRE_SEVERITY),
  templates: z.record(z.enum(WIRE_TEMPLATE_KEYS), z.array(templateText(WIRE_SLOTS)).min(2).max(4)),
  causes: z.record(z.enum(CAUSE_KIND_NAMES), z.array(templateText(WIRE_SLOTS.filter((s) => s !== 'cause'))).min(1).max(4)),
  priceCauses: z.record(z.enum(PRICE_CAUSE_TAGS), templateText([...WIRE_SLOTS.filter((s) => s !== 'cause'), 'pct'])),
})
export type WireConfig = z.infer<typeof wireConfigSchema>

// ---------------------------------------------------------------------------------------------
// local.json (spec §10)
// ---------------------------------------------------------------------------------------------

export const localConfigSchema = z
  .strictObject({
    comment,
    maxRealShips: posInt,
    promoteM: positive,
    demoteM: positive,
    demoteCalmS: nonNegative,
    syncS: positive,
    grudgeS: positive,
    undockSpeed: positive,
    undockCorridorM: positive,
    sublightFraction: unit,
    cruiseClearM: positive,
    approachDropM: positive,
    dockStartM: positive,
    mineStandoffM: positive,
    minePower: unit,
    holdOrbitM: positive,
    threatM: positive,
    calmM: positive,
    calmS: positive,
    arriveGain: positive,
    arriveFraction: unit,
    orbitLead: nonNegative,
    dangerGain: nonNegative,
    dangerRateGain: nonNegative,
    raiderShare: unit,
    aiProfiles: z.record(roleIdSchema, z.array(idString).min(1)),
  })
  .refine((l) => l.demoteM > l.promoteM, { message: 'demoteM must exceed promoteM (hysteresis)' })
export type LocalConfig = z.infer<typeof localConfigSchema>

// ---------------------------------------------------------------------------------------------
// Commands (spec §11.3)
// ---------------------------------------------------------------------------------------------

const devCommandSchema = z.discriminatedUnion('op', [
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceShortage'), stationId: idString, ware: wareIdSchema }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('spawnRaid'), systemId: idString }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('loseNextConvoy') }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('runDays'), days: posInt }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('setStability'), value: z.number().min(0).max(100) }),
  // Playtest FC-R2-2 and FC-R2-3.
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceWorks'), systemId: idString.optional() }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('starveLine'), stationId: idString, line: nonNegInt.optional() }),
  // Playtest R3-H3.
  z.strictObject({ type: z.literal('dev'), op: z.literal('postShortage'), stationId: idString, ware: wareIdSchema }),
])

export const strategicCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('playerTrade'),
    stationId: idString,
    ware: wareIdSchema,
    qty: positive,
    side: z.enum(['buy', 'sell']),
    expectedUnit: positive,
    /** M2 standing (the ±5% modifier, spec §5.4). */
    standing: z.number(),
  }),
  z.strictObject({ type: z.literal('playerMined'), systemId: idString, fieldId: idString, ware: wareIdSchema, qty: positive }),
  z.strictObject({ type: z.literal('playerSoldComponent'), stationId: idString, component: componentTypeSchema, condition: unit }),
  z.strictObject({ type: z.literal('localStepComplete'), agentId: idString }),
  z.strictObject({ type: z.literal('localReleased'), agentId: idString }),
  z.strictObject({
    type: z.literal('localDestroyed'),
    agentId: idString,
    killer: z.union([z.literal('player'), factionIdSchema, z.null()]),
    selfDefence: z.boolean().optional(),
  }),
  z.strictObject({ type: z.literal('setRelayOnline'), relayId: idString, online: z.boolean() }),
  z.strictObject({ type: z.literal('setWarp'), mult: positive }),
  devCommandSchema,
]) satisfies z.ZodType<StrategicCommand>

/**
 * M8 (plan §4.5 SNK-3a): the pilot's sink draw (`economy/market.ts playerDraw`). Its own schema beside the M4 union
 * (whose type is `StrategicCommand`); `commands.ts parseCommand` tries it too. Only the pilot's draw categories.
 */
export const playerDrawCommandSchema = z.strictObject({
  type: z.literal('playerDraw'),
  stationId: idString,
  ware: wareIdSchema,
  wantT: positive,
  expectedUnit: positive,
  /** M2 standing (the ±5% modifier, spec §5.4). */
  standing: z.number(),
  sink: z.enum(['playerRepair', 'playerTorpedo']),
  /** Pre-tariff credits the wallet covers; the draw never costs more. */
  maxTotal: nonNegative.optional(),
}) satisfies z.ZodType<PlayerDrawCommand>

/**
 * M8 (plan §4.5 SNK-6b1): the pilot's hand-in to a works site (`factions/requests.ts worksDeliver`). Its own schema
 * like `playerDrawCommandSchema`; `commands.ts parseCommand` tries it too. The worker prices it; `expectedReward` is
 * the client's quote, checked to the 2% player tolerance.
 */
export const worksDeliverCommandSchema = z.strictObject({
  type: z.literal('worksDeliver'),
  siteId: idString,
  ware: wareIdSchema,
  qty: positive,
  expectedReward: positive,
}) satisfies z.ZodType<WorksDeliverCommand>

/**
 * M9 (plan CH-1b): the player opens or abandons a claim on a disabled off-screen wreck (`combat/wrecks.ts markClaim`).
 * Its own schema like `worksDeliverCommandSchema`; `commands.ts parseCommand` tries it too.
 */
export const markClaimCommandSchema = z.strictObject({
  type: z.literal('markClaim'),
  wreckId: idString,
  open: z.boolean(),
}) satisfies z.ZodType<MarkClaimCommand>

/** A hulk's flaws bitmask: the worker's Uint16 `FleetStore.flaws` (bits 0–11 flaws, 12–15 the FR edge; CH-4). */
const hullFlawsSchema = z.number().int().min(0).max(0xffff)
/** A claimed hull's value factor: (0, 1], 1 = pristine (CH-8). */
const hullValueMultSchema = z.number().gt(0).max(1)
/**
 * A hulk key: a seeded hulk's `hk:<sys>:<n>` (CH-2a) or an off-screen wreck's `ow-<seq>` (CH-3b). Serials start at 0
 * (`ids.ts offscreenWreckId`): R3-2, `ow-0` was refused `invalid` here, so the galaxy's first claimable loss could
 * never be claimed.
 */
const hulkKeySchema = z.string().regex(/^(hk:.+|ow-(?:0|[1-9]\d*))$/)

/**
 * M9 (plan CH-7a): register, fix and scrap a claimed ship (`fleet/claims.ts`). Its own schema like
 * `markClaimCommandSchema`; `commands.ts parseCommand` tries it too.
 */
export const claimedShipCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('registerClaimedShip'),
    hulkKey: hulkKeySchema,
    hull: idString,
    flaws: hullFlawsSchema,
    valueMult: hullValueMultSchema,
    systemId: idString,
    pos: vec3,
    dockAt: idString,
  }) satisfies z.ZodType<RegisterClaimedShipCommand>,
  z.strictObject({
    type: z.literal('setHullFlaws'),
    shipId: idString,
    flaws: hullFlawsSchema,
    valueMult: hullValueMultSchema,
    stationId: idString,
  }) satisfies z.ZodType<SetHullFlawsCommand>,
  z.strictObject({ type: z.literal('scrapClaimedShip'), shipId: idString }) satisfies z.ZodType<ScrapClaimedShipCommand>,
]) satisfies z.ZodType<ClaimedShipCommand>

// ---------------------------------------------------------------------------------------------
// Views, the handoff and the Wire (spec §10.1, §11.2, §12, §13)
// ---------------------------------------------------------------------------------------------

const wareLotSchema = z.strictObject({ ware: wareIdSchema, qty: nonNegative })

export const nodeRefSchema = z.strictObject({ id: idString, kind: z.enum(NODE_KIND_NAMES), pos: vec3 }) satisfies z.ZodType<NodeRef>

export const localStepSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('docked'), station: idString, until: z.number() }),
  z.strictObject({
    kind: z.literal('leg'),
    from: nodeRefSchema,
    to: nodeRefSchema,
    t0: z.number(),
    t1: z.number(),
    purpose: z.enum(LEG_PURPOSE_NAMES),
  }),
  z.strictObject({ kind: z.literal('mining'), field: idString, until: z.number() }),
  z.strictObject({ kind: z.literal('holding'), node: nodeRefSchema, until: z.number() }),
]) satisfies z.ZodType<LocalStep>

const localHex = z.string().regex(/^#[0-9a-f]{6}$/i, { message: 'expected a #rrggbb colour' })

/** M6 (plan P16): a player ship's block in the local manifest. */
export const localFleetSchema = z.strictObject({
  shipId: idString,
  name: z.string(),
  captainId: idString.nullable(),
  skills: z.array(z.number().int().min(0).max(5)),
  order: z.enum(ORDER_NAMES),
  roe: z.enum(ROE_NAMES),
  livery: z.strictObject({ primary: localHex, secondary: localHex, accent: localHex }),
  escortPlayer: z.literal(true).optional(),
}) satisfies z.ZodType<LocalFleet>

export const localAgentSchema = z.strictObject({
  id: idString,
  role: roleIdSchema,
  faction: factionIdSchema,
  teamId: nonNegInt,
  hull: idString,
  shipDef: idString,
  hp: unit,
  cargo: wareLotSchema.nullable(),
  groupId: idString.nullable(),
  step: localStepSchema,
  fleet: localFleetSchema.optional(),
  job: z.strictObject({ kind: z.enum(['carry', 'load', 'standby']), ware: wareIdSchema.nullable(), to: idString }).optional(),
}) satisfies z.ZodType<LocalAgent>

export const localSyncSchema = z.strictObject({ id: idString, hp: unit, cargoQty: nonNegative, progress: unit }) satisfies z.ZodType<LocalSync>

export const localDeltaSchema = z.strictObject({
  spawns: z.array(localAgentSchema),
  steps: z.array(z.strictObject({ id: idString, step: localStepSchema, job: localAgentSchema.shape.job })),
}) satisfies z.ZodType<LocalDelta>

export const priceCauseSchema = z.strictObject({ tag: z.enum(PRICE_CAUSE_TAGS), deltaPct: z.number(), text: idString }) satisfies z.ZodType<PriceCause>

export const marketRowSchema = z.strictObject({
  ware: wareIdSchema,
  buys: z.boolean(),
  sells: z.boolean(),
  stock: nonNegative,
  capacity: nonNegative,
  fill: unit,
  sellUnit: positive.nullable(),
  buyUnit: positive.nullable(),
  floorUnit: positive.optional(),
  reserved: nonNegative.optional(),
  trend: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
  cause: priceCauseSchema.nullable(),
}) satisfies z.ZodType<MarketRow>

const stageViewSchema = z.enum(['pending', 'active', 'recovering'])

/** A claim view's faction: an AI faction or `'player'` for the corporation (D-424, M5 spec §27.7). */
const claimFactionIdSchema = z.union([factionIdSchema, z.literal('player')])

/** M5 spec §16; §27.7 (D-424) widens the factions to the corporation and adds `corp`. */
export const claimViewSchema = z.strictObject({
  systemId: idString,
  holder: claimFactionIdSchema.nullable(),
  tariff: nonNegative,
  toll: nonNegative,
  contested: z.boolean(),
  flip: z.strictObject({ challenger: claimFactionIdSchema, daysLeft: nonNegative }).nullable(),
  scores: z.array(
    z.strictObject({ faction: claimFactionIdSchema, ops: z.number(), sec: z.number(), inf: z.number(), s: z.number(), trend: z.array(z.number()) }),
  ),
  states: z.array(z.strictObject({ kind: z.enum(STATE_KIND_NAMES), stage: stageViewSchema, daysLeft: z.number() })),
  corp: z.strictObject({ name: z.string(), band: z.enum(['low', 'medium', 'high']), tariffSet: z.boolean() }).nullable(),
}) satisfies z.ZodType<ClaimView>

export const stationViewSchema = z.strictObject({
  id: idString,
  name: idString,
  systemId: idString,
  template: idString,
  assetId: idString,
  owner: factionIdSchema,
  pos: vec3,
  dockable: z.boolean(),
  wares: z.array(marketRowSchema),
  lines: z.array(
    z.strictObject({ recipe: idString, status: z.enum(LINE_STATUS_NAMES), missing: wareIdSchema.nullable(), progress: unit }),
  ),
  workforceMult: positive,
  habitat: z.strictObject({ satisfaction: unit }).nullable(),
  shipyard: z
    .strictObject({ queue: z.array(z.strictObject({ hull: idString, faction: factionIdSchema, eta: z.number().nullable() })) })
    .nullable(),
  construction: z.null(),
  holder: factionIdSchema.nullable(),
  tariff: nonNegative,
  disabledBy: factionIdSchema.nullable(),
  banner: z
    .strictObject({ kind: z.union([z.enum(STATE_KIND_NAMES), z.literal('disabled')]), stage: stageViewSchema, text: idString })
    .nullable(),
  miners: z.strictObject({ stoodDown: nonNegInt, booked: nonNegInt, haulersWaiting: nonNegInt }).optional(),
}) satisfies z.ZodType<StationView>

export const siteViewSchema = z.strictObject({
  id: idString,
  systemId: idString,
  template: idString,
  level: posInt,
  owner: factionIdSchema,
  pos: vec3,
  stage: z.enum(SITE_STAGE_NAMES),
  required: wareAmounts,
  delivered: wareAmounts,
  buildDays: nonNegative,
  progressDays: nonNegative,
}) satisfies z.ZodType<SiteView>

export const factionRequestSchema = z.strictObject({
  id: idString,
  faction: factionIdSchema,
  systemId: idString,
  stationId: idString.nullable(),
  kind: z.enum(REQUEST_KIND_NAMES),
  ware: wareIdSchema.optional(),
  qty: positive.optional(),
  zoneId: idString.optional(),
  reward: nonNegative,
  expiresDay: nonNegInt,
}) satisfies z.ZodType<FactionRequest>

/**
 * A request record from save v5 on (M8 SNK-6b1): the v4 record plus the works request's optional `siteId`, `unit` and
 * `openedDay` (the frozen v4 schema is extended, not edited; `save.ts strategicSaveV5Schema` uses it).
 */
export const factionRequestV5Schema = factionRequestSchema.extend({
  siteId: idString.optional(),
  unit: positive.optional(),
  openedDay: nonNegInt.optional(),
}) satisfies z.ZodType<FactionRequest>

/** A request row on the system view: a works request also carries today's `premium` (M8 SNK-6b1). */
export const factionRequestViewSchema = factionRequestV5Schema.extend({
  premium: positive.optional(),
}) satisfies z.ZodType<FactionRequestView>

export const systemEconomyViewSchema = z.strictObject({
  systemId: idString,
  stations: z.array(stationViewSchema),
  sites: z.array(siteViewSchema),
  danger: unit,
  agentsByRole: z.record(roleIdSchema, nonNegInt),
  fields: z.array(z.strictObject({ id: idString, reserves: wareAmounts })),
  claim: claimViewSchema,
  zones: z.array(
    z.strictObject({
      id: idString,
      sideA: factionIdSchema,
      sideB: factionIdSchema,
      node: nodeRefSchema,
      opened: z.number(),
      until: z.number(),
      strengthA: nonNegative,
      strengthB: nonNegative,
    }),
  ),
  requests: z.array(factionRequestViewSchema),
  tetherstoneIllegal: z.boolean(),
}) satisfies z.ZodType<SystemEconomyView>

const wareFill = z.strictObject({ ware: wareIdSchema, fill: unit }).nullable()
export const galaxyEconomyViewSchema = z.strictObject({
  systems: z.array(
    z.strictObject({
      id: idString,
      worstShortage: wareFill,
      topSurplus: wareFill,
      danger: unit,
      productionValue: nonNegative,
      tradeVolume: nonNegative,
      agents: nonNegInt,
      prices: z.instanceof(Float32Array).refine((p) => p.length === WARE_IDS.length, { message: 'prices holds one mean price per ware' }),
      charter: z
        .strictObject({
          holder: claimFactionIdSchema.nullable(),
          contested: z.boolean(),
          tariff: nonNegative,
          toll: nonNegative,
          states: z.array(z.strictObject({ kind: z.enum(STATE_KIND_NAMES), stage: stageViewSchema })),
          zones: nonNegInt,
        })
        .optional(),
    }),
  ),
  relays: z.array(z.strictObject({ id: idString, online: z.boolean(), flowT: nonNegative })),
  fronts: z.array(z.strictObject({ relayId: idString, a: factionIdSchema, b: factionIdSchema })).optional(),
  // M8 SNK-6c: the open works requests (always set by `buildGalaxyView`; optional like `fronts`).
  works: z
    .array(
      z.strictObject({
        systemId: idString,
        siteId: idString,
        faction: factionIdSchema,
        ware: wareIdSchema,
        missingT: positive,
        premium: positive,
        daysOpen: nonNegInt,
      }),
    )
    .optional(),
}) satisfies z.ZodType<GalaxyEconomyView>

export const wireItemSchema = z.strictObject({
  id: nonNegInt,
  t: nonNegative,
  day: nonNegInt,
  kind: z.enum(WIRE_KINDS),
  severity: WIRE_SEVERITY,
  systemId: idString,
  stationId: idString.optional(),
  ware: wareIdSchema.optional(),
  factions: z.array(factionIdSchema),
  headline: idString,
  cause: z.strictObject({ kind: z.enum(CAUSE_KIND_NAMES), text: z.string() }).optional(),
  /** M7 (S16): the outlet (S17 writes it). */
  source: z.enum(STORY_WIRE_OUTLETS).optional(),
}) satisfies z.ZodType<WireItem>

export const offscreenWreckSchema = z.strictObject({
  id: idString,
  systemId: idString,
  pos: vec3,
  seed: nonNegInt,
  hull: idString,
  cargo: wareLotSchema.nullable(),
  createdAt: nonNegative,
  expiresAt: nonNegative,
}) satisfies z.ZodType<OffscreenWreck>

/** M8 (MC-f5c): a wreck owner is −1 or a maker faction's index (never the raiders' or the guardian cell's). */
const WRECK_OWNER_VALUES: readonly number[] = [WRECK_OWNER_NONE, ...WRECK_MAKER_IDS.map((id) => FACTION[id])]
const wreckOwnerSchema = z
  .number()
  .int()
  .refine((v) => WRECK_OWNER_VALUES.includes(v), { message: 'expected -1 or a maker faction index' })

/** M8 (MC-f5c): a v5 save's wreck record, the v1–v4 record (frozen above) plus `owner`. */
export const savedOffscreenWreckSchema = offscreenWreckSchema.extend({ owner: wreckOwnerSchema }) satisfies z.ZodType<SavedOffscreenWreck>

/** M9 (CH-1a): a saved wreck's claim state, 1 (disabled) or 2 (open claim); absent = 0 (`WRECK_CLAIM`). */
export const wreckClaimSchema = z.union([z.literal(WRECK_CLAIM.disabled), z.literal(WRECK_CLAIM.open)])

/** M9 (CH-1a): a v6 save's wreck record, the v5 record (frozen above) plus the optional `claim`. */
export const savedOffscreenWreckV6Schema = savedOffscreenWreckSchema.extend({ claim: wreckClaimSchema.optional() }) satisfies z.ZodType<SavedOffscreenWreck>

/** M8 (MC-f5c): a manifest wreck: the v6 record (M9 adds `claim`) plus its `maker` when it has one. */
export const manifestWreckSchema = savedOffscreenWreckV6Schema.extend({ maker: z.enum(WRECK_MAKER_IDS).optional() }) satisfies z.ZodType<OffscreenWreck>

export const localManifestSchema = z.strictObject({
  systemId: idString,
  time: nonNegative,
  stations: z.array(stationViewSchema),
  agents: z.array(localAgentSchema),
  wrecks: z.array(manifestWreckSchema),
  zones: z.array(
    z.strictObject({
      id: idString,
      sideA: factionIdSchema,
      sideB: factionIdSchema,
      node: nodeRefSchema,
      until: z.number(),
      agentsA: z.array(idString),
      agentsB: z.array(idString),
    }),
  ),
  warBands: z.array(idString),
}) satisfies z.ZodType<LocalManifest>

/** M6 away-report fleet lines and totals (plan P13). */
export const awayFleetLineSchema = z.strictObject({
  kind: z.enum(['earning', 'loss', 'captain', 'claim', 'station', 'milestone']),
  day: nonNegInt,
  text: idString,
  shipId: idString.optional(),
  captainId: idString.optional(),
  systemId: idString.optional(),
  amount: z.number().optional(),
}) satisfies z.ZodType<AwayFleetLine>
export const awayFleetSummarySchema = z.strictObject({
  earnings: z.number(),
  wages: z.number(),
  costs: z.number(),
  net: z.number(),
  shipsLost: nonNegInt,
  lossValue: nonNegative,
  captainsLost: nonNegInt,
  claimsGained: nonNegInt,
  claimsLost: nonNegInt,
  claimChanges: z
    .array(z.strictObject({ systemId: idString, system: idString, change: z.enum(['gained', 'lost']), holder: z.string() }))
    .optional(),
}) satisfies z.ZodType<AwayFleetSummary>

export const awayReportSchema = z.strictObject({
  awaySeconds: nonNegative,
  simulatedDays: nonNegInt,
  capped: z.boolean(),
  headlines: z.array(wireItemSchema).max(8),
  markets: z.array(z.strictObject({ ware: wareIdSchema, stationId: idString, fromUnit: nonNegative, toUnit: nonNegative })),
  traffic: z.strictObject({ built: nonNegInt, lost: nonNegInt, convoysLost: nonNegInt }),
  newStations: z.array(z.strictObject({ id: idString, name: idString, systemId: idString })),
  fleet: z.array(awayFleetLineSchema),
  fleetSummary: awayFleetSummarySchema.optional(),
}) satisfies z.ZodType<AwayReport>

// ---------------------------------------------------------------------------------------------
// The v1 save (spec §14)
// ---------------------------------------------------------------------------------------------

/** Standard base64 (typed-array columns and bitsets). */
export const base64Schema = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/, { message: 'expected base64' })

export const shipOrderSchema = z.strictObject({
  id: idString,
  faction: factionIdSchema,
  hull: idString,
  role: roleIdSchema,
  home: idString.nullable(),
  yard: idString,
  price: nonNegative,
  /** Balance pass T4: owed on delivery; older saves were prepaid (0). */
  due: nonNegative.default(0),
  /** Balance pass T4: a fracture-rig replacement miner. */
  rig: z.boolean().default(false),
  placedAt: nonNegative,
  startedAt: nonNegative.nullable(),
  eta: nonNegative.nullable(),
}) satisfies z.ZodType<ShipOrder>

export const constructionSiteSchema = z.strictObject({
  id: idString,
  systemId: idString,
  template: idString,
  level: posInt,
  owner: factionIdSchema,
  position: vec3,
  required: wareAmounts,
  delivered: wareAmounts,
  stage: z.enum(SITE_STAGE_NAMES),
  buildDays: nonNegative,
  progressDays: nonNegative,
  startDay: nonNegInt,
}) satisfies z.ZodType<ConstructionSite>

export const groupRecordSchema = z.strictObject({
  id: idString,
  kind: z.enum(GROUP_KIND_NAMES),
  state: nonNegInt,
  faction: factionIdSchema,
  leader: idString,
  members: z.array(idString),
  target: z.number().int(),
  home: idString.nullable(),
  timer: z.number(),
}) satisfies z.ZodType<GroupRecord>

export const dayRollupRecordSchema = z.strictObject({
  day: nonNegInt,
  built: nonNegInt,
  lost: nonNegInt,
  convoysLost: nonNegInt,
  ordersPlaced: nonNegInt,
  newStations: z.array(idString),
  tetherstoneExtracted: nonNegative,
  tetherstonePlayer: nonNegative.optional(),
  stability: nonNegative,
  cols: z.record(idString, base64Schema),
}) satisfies z.ZodType<DayRollupRecord>

export const strategicSaveV1Schema = z.strictObject({
  version: z.literal(1),
  galaxySeed: nonNegInt,
  seed: nonNegInt,
  time: nonNegative,
  tick: nonNegInt,
  rngState: nonNegInt,
  counters: z.strictObject({
    agentSeq: nonNegInt,
    wreckSeq: nonNegInt,
    wireSeq: nonNegInt,
    siteSeq: nonNegInt,
    groupSeq: nonNegInt,
    orderSeq: nonNegInt,
    encounterSeq: nonNegInt,
  }),
  relaysOnline: base64Schema,
  stations: z.strictObject({
    meta: z.array(
      z.strictObject({
        id: idString,
        template: idString,
        owner: factionIdSchema,
        level: posInt,
        name: idString,
        node: z.number().int(),
        /** M6: a player station's outpost anchor (construction `anchorKindNear`); absent = 'other'. */
        anchor: z.enum(['asteroid', 'ice', 'gas', 'other']).optional(),
      }),
    ),
    cols: z
      .object({
        stock: base64Schema,
        reserved: base64Schema,
        incoming: base64Schema,
        baseline: base64Schema,
        lineProgress: base64Schema,
        habitat: base64Schema,
      })
      .catchall(base64Schema),
  }),
  agents: z.strictObject({ count: nonNegInt, cols: z.record(idString, base64Schema) }),
  systems: z.strictObject({ cols: z.record(idString, base64Schema) }),
  groups: z.array(groupRecordSchema),
  orders: z.array(shipOrderSchema),
  sites: z.array(constructionSiteSchema),
  wrecks: z.array(offscreenWreckSchema),
  fields: base64Schema,
  factions: z.array(z.strictObject({ id: factionIdSchema, treasury: z.number(), ledger: z.array(z.number()) })),
  gate: z.strictObject({ stability: nonNegative, extractedToday: nonNegative, extractionEma: nonNegative.optional(), playerEma: nonNegative.optional(), playerExtractedToday: nonNegative.optional(), storyMult: nonNegative.optional(), history: z.array(z.number()) }),
  wire: z.array(wireItemSchema),
  rollups: z.array(dayRollupRecordSchema),
}) satisfies z.ZodType<StrategicSaveV1>


// =============================================================================================
// M5: the Charter data files, commands and the v2 save (M5 spec; plan C0). data.ts cross-checks references.
// =============================================================================================

const signedStanding = z.number().min(-100).max(100)
const wareQtyStrict = z.partialRecord(wareIdSchema, positive)

/** claims.json (M5 spec §5). */
export const claimsConfigSchema = z
  .strictObject({
    comment,
    emaKeep: unit,
    trendDays: posInt.max(255),
    weights: z.strictObject({ ops: unit, sec: unit, inf: unit }),
    operations: z.strictObject({ tradeShare: unit }),
    influence: z.strictObject({
      decayPerDay: unit,
      agentTradeCredits: positive,
      restartDelivery: nonNegative,
      relinkSponsor: nonNegative,
      zoneWin: nonNegative,
      playerContractCredits: positive,
      playerTradeCredits: positive,
      reputationPerDay: nonNegative,
      isolationHoldfastPerDay: nonNegative,
      /** M7 (plan S20): the one-off restart influence of a registered First Rush restart (points). */
      reactivation: nonNegative,
    }),
    holder: z.strictObject({
      claimAt: z.number().min(0).max(100),
      lead: z.number().min(0).max(100),
      flipDays: posInt,
      lapseBelow: z.number().min(0).max(100),
      lapseDays: posInt,
      contestedMin: z.number().min(0).max(100),
      contestedGap: z.number().min(0).max(100),
    }),
    tariff: z
      .strictObject({ feesMult: unit, warAdd: unit, fallingTradeCut: unit, fallingTradeDays: posInt, min: unit, max: unit })
      .refine((t) => t.min <= t.max, { message: 'tariff: min ≤ max' }),
    toll: z.strictObject({
      minFees: unit,
      credits: z.strictObject({ light: nonNegative, medium: nonNegative, large: nonNegative }),
      hullClass: z.record(idString, z.enum(HULL_CLASS_NAMES)),
    }),
    subsidy: z.strictObject({
      base: nonNegative,
      opsShare: nonNegative,
      cap: nonNegative,
      isolationMult: unit,
      /**
       * Balance pass T5: when > 0 the subsidy is × clamp(Authority treasury ÷ its start, 0, fundScaleMax), so the
       * Authority pays more when its fund is full and nothing when it is empty. 0 = off (× 1).
       */
      fundScaleMax: nonNegative.default(0),
    }),
    fees: z.strictObject({ rate: unit }),
    /**
     * Dev only (D-TBD keep claims hard): the dev `forceClaim` to the player corporation holds its S at no less than
     * `seedScore` for `holdDays` faction days (0 = off). Normal play never sets it.
     */
    devForceClaim: z
      .strictObject({ seedScore: z.number().min(0).max(100), holdDays: nonNegInt })
      .default({ seedScore: 70, holdDays: 10 }),
    /**
     * Balance pass T5 (D-314 D1, closed money): the daily Charter levy and relief, transfers through the Authority.
     * Each non-outlaw faction pays `rate` × (treasury − `above` × start) when positive; the Authority pays a faction
     * below `reliefBelow` × start `reliefRate` × the gap, out of its own non-negative funds. Rates 0 = off.
     */
    levy: z
      .strictObject({ rate: unit, above: nonNegative, reliefRate: unit, reliefBelow: nonNegative })
      .default({ rate: 0, above: 1, reliefRate: 0, reliefBelow: 0 }),
  })
  .refine((c) => Math.abs(c.weights.ops + c.weights.sec + c.weights.inf - 1) < 1e-6, { message: 'claims: weights must sum to 1' })
export type ClaimsConfig = z.infer<typeof claimsConfigSchema>

/** states.json (M5 spec §6). */
export const statesConfigSchema = z.strictObject({
  comment,
  pendingDays: posInt,
  recoveringDays: nonNegInt,
  recoveringEffect: unit,
  boom: z.strictObject({ productionVsMean: positive, tradeVsMean: positive, meanDays: posInt, activeDays: posInt, productionMult: positive }),
  bust: z.strictObject({ starvedLineShare: unit, starvedDayShare: unit, daysRunning: posInt, activeDays: posInt, productionMult: unit }),
  famine: z.strictObject({ foodBelow: unit, daysRunning: posInt, activeDays: posInt, workforceMult: unit }),
  strike: z.strictObject({ grievanceAt: z.number().min(0).max(100), activeDays: posInt }),
  lockdown: z.strictObject({
    losses: posInt,
    wantedLevel: z.number().int().min(1).max(3),
    activeDays: posInt,
    patrolMult: positive,
    legDelayMult: positive,
    dockExtraS: nonNegative,
  }),
  blockade: z.strictObject({ securityShare: unit, raiderRatio: positive, minDays: posInt, interceptPerShare: unit, importMult: unit }),
  grievance: z.strictObject({
    faminePerDay: nonNegative,
    megacorpTariffAt: unit,
    megacorpTariffPerDay: nonNegative,
    perMutualLoss: nonNegative,
    mutualLossCapPerDay: nonNegative,
    decayPerDay: nonNegative,
    max: positive,
  }),
  /** Factions whose stations a Strike stops. */
  megacorps: z.array(factionIdSchema).min(1),
})
export type StatesConfig = z.infer<typeof statesConfigSchema>

const hysteresis = z.strictObject({ up: z.number().min(0).max(100), down: z.number().min(0).max(100) }).refine((h) => h.down < h.up, {
  message: 'relation hysteresis: down < up',
})

/** ladder.json (M5 spec §8). */
export const ladderConfigSchema = z
  .strictObject({
    comment,
    referee: factionIdSchema,
    pressure: z.strictObject({
      max: positive,
      perContest: nonNegative,
      claimFlip: nonNegative,
      perKill: nonNegative,
      killCapPerDay: nonNegative,
      press: nonNegative,
      stoke: nonNegative,
      conflictMult: nonNegative,
      lowTreasury: z.number(),
      lowTreasuryShare: unit,
      weariness: z.number(),
      decayPerDay: nonNegative,
      /** Extra decay per day while the pair is at war (balance pass T1, D-314); 0 = off. */
      warDecayPerDay: nonNegative.default(0),
      /**
       * Balance round 2 R2-T4 (D-TBD): false = the kill term adds no pressure while the pair is already at war. A war's
       * own kills otherwise feed the pressure that keeps it going (up to killCapPerDay a day); weariness counts them.
       */
      killsAtWar: z.boolean().default(true),
      /** R2-T4 (D-TBD): pressure a pair falls to when its war ends (a settlement), if higher; max = off. */
      afterWar: nonNegative.default(100),
    }),
    relations: z.strictObject({ economic: hysteresis, skirmish: hysteresis, war: hysteresis }),
    war: z.strictObject({ minTreasury: nonNegative, minDays: posInt }),
    economic: z.strictObject({ buyMult: positive, sellMult: positive }),
    disable: z.strictObject({ attackerShare: unit, ownerMax: unit, days: posInt, reenableShare: unit, reenableDays: posInt }),
    warBand: z.strictObject({ hulls: z.record(idString, posInt), upkeepPerDay: nonNegative, patrolTargetsPerBand: positive, meetChance: unit }),
  })
  .refine((l) => l.relations.economic.up < l.relations.skirmish.up && l.relations.skirmish.up < l.relations.war.up, {
    message: 'ladder: economic.up < skirmish.up < war.up',
  })
export type LadderConfig = z.infer<typeof ladderConfigSchema>

/** ai.json (M5 spec §7). */
export const aiConfigSchema = z.strictObject({
  comment,
  maxActionsPerDay: posInt,
  minScore: nonNegative,
  cooldownDays: z.strictObject(Object.fromEntries(ACTION_NAMES.map((a) => [a, nonNegInt])) as Record<(typeof ACTION_NAMES)[number], typeof nonNegInt>),
  build: z.strictObject({
    shortFill: unit,
    shortDays: posInt,
    windowDays: posInt,
    minStations: posInt,
    treasuryMult: positive,
    maxActiveSites: posInt,
    costWeight: nonNegative,
    /**
     * Balance round 2 R2-T2 (D-335 D1), all off by default. `supplyGate`: a build also needs the region's
     * producers of the ware short (mean end-of-day seller fill over `windowDays` < `sellerShortFill`; no
     * producers passes) and at least one of the template's outputs with room (regional seller fill <
     * `outputFullFill`). `growthCap`: a faction's stations plus active sites stay below floor(start × growthCap)
     * for `expand` and `expandReserve` fewer for `build` (0 = no cap; start = its seeded stations).
     */
    supplyGate: z.boolean().default(false),
    sellerShortFill: unit.default(0.3),
    outputFullFill: unit.default(0.8),
    growthCap: nonNegative.default(0),
    expandReserve: nonNegInt.default(1),
  }),
  expand: z.strictObject({ minStations: posInt, jumps: posInt, holderBelow: z.number().min(0).max(100), patrols: nonNegInt, patrolDays: nonNegInt }),
  /**
   * `atWar`: may a faction press a rival it is already at war with (balance pass T1, D-314; true = the M5 behaviour).
   * `truceDays` (R2-T4 part 3, D-TBD): no `press` against a rival for this many faction days after a war with it
   * ends (0 = off). Without it, post-war presses (+10) re-pressurise the pair and the war re-enters in 3-4 days.
   * `contestRule` (LV WR-B4, bug B4, D-TBD): which systems count as the pair's contests for `press` (n, stake and the
   * first system). `any` (absent): every system where both sit at S ≥ contestedMin with a gap under contestedGap.
   * `topTwo`: only systems whose top-two pair (`ladder.ts` `contestPair`, the rule the war gate and the pressure
   * terms read) is the pair itself. Optional with no default, so the parsed config is unchanged when absent.
   * `stress` (LV WR-1a, D7 rule 2, D-TBD): the reference `treasuryStress` measures against. `start` (absent): 1 −
   * treasury ÷ start treasury. `window`: against the highest end-of-day treasury over the saved day rollups
   * (`state.rollups`, the last strategic.json `rollupDays`) and today's. `floor`: against start × `floorFrac`.
   * `windowFloor`: the larger stress of `window` and `floor`. `floorFrac` is required by `floor` and `windowFloor`.
   * `waves` (LV WR-1a, D7 rules 1 and 3, D-TBD): after a war ends, the pair's peace stretch lasts `peaceMin` +
   * (a hash of the pair and its saved `warEndDay`) mod (`peaceMax` − `peaceMin` + 1) days from the war's end, during
   * which press is offered only while the pair's pressure is below `simmerCap` (it may climb to skirmish, not war).
   * `truceDays ≤ peaceMin ≤ peaceMax`. `simmerCap` must sit below ladder.json `relations.war.up` (ladder.json parses
   * separately, so the cross-file check is in data.ts `parseCharterData`, which throws; a cap at or above war entry lets
   * press lift the pair into war inside the stretch). `lullDays` + `lullBonus` (both or neither; absent = off): when
   * no pair is at war and the Reach's last war ended ≥ `lullDays` ago (counted from day 0 when none has), the press
   * candidate against the faction's most top-two-contested rival gains `lullBonus`. Both keys optional with no
   * default, so the parsed config is unchanged when absent.
   */
  press: z
    .strictObject({
      pressure: nonNegative,
      contestsNorm: positive,
      atWar: z.boolean().default(true),
      truceDays: nonNegInt.default(0),
      contestRule: z.enum(['any', 'topTwo']).optional(),
      stress: z
        .strictObject({ ref: z.enum(['start', 'window', 'floor', 'windowFloor']), floorFrac: z.number().gt(0).max(1).optional() })
        .refine((s) => (s.ref !== 'floor' && s.ref !== 'windowFloor') || s.floorFrac !== undefined, {
          message: 'press.stress: floor and windowFloor need floorFrac',
        })
        .optional(),
      waves: z
        .strictObject({
          peaceMin: nonNegInt,
          peaceMax: nonNegInt,
          simmerCap: z.number().min(0).max(100),
          lullDays: posInt.optional(),
          lullBonus: nonNegative.optional(),
        })
        .refine((w) => w.peaceMin <= w.peaceMax, { message: 'press.waves: peaceMin ≤ peaceMax' })
        .refine((w) => (w.lullDays === undefined) === (w.lullBonus === undefined), {
          message: 'press.waves: lullDays and lullBonus come together',
        })
        .optional(),
    })
    .refine((p) => p.waves === undefined || p.truceDays <= p.waves.peaceMin, { message: 'press.waves: truceDays ≤ peaceMin' }),
  seekPeace: z.strictObject({ pressure: z.number().max(0) }),
  relink: z.strictObject({
    newSystemsNorm: positive,
    tradeWeight: nonNegative,
    rebootS: positive,
    maxActive: posInt,
    longRelayMult: positive,
    cost: z.strictObject({ gate: wareQtyStrict, mid: wareQtyStrict, deep: wareQtyStrict, rim: wareQtyStrict }),
  }),
  sweep: z.strictObject({ jumps: posInt, minDanger: unit }),
  stoke: z.strictObject({ faction: factionIdSchema, minTreasury: nonNegative, maxPressure: nonNegative, pressure: nonNegative, score: nonNegative }),
  jobs: z.strictObject({
    skirmishPatrolAdd: nonNegative,
    warPatrolAdd: nonNegative,
    lockdownPatrolAdd: nonNegative,
    tremorAuthorityGateMult: positive,
    isolationAuthorityMult: nonNegative,
    tetherstonePolicy: z.strictObject({
      priceMin: positive,
      priceMax: positive,
      banMult: unit,
      guardianLossDays: posInt,
      guardianLossesHigh: posInt,
      guardianLossMult: unit,
      minersPerEscort: posInt,
    }),
    warBandsPerPatrolTargets: positive,
    constructorsPerSite: nonNegInt,
  }),
  requests: z.strictObject({
    perFactionPerDay: nonNegInt,
    capacity: posInt,
    expiresDays: posInt,
    deliverValueMult: positive,
    clearRaidersReward: numRange,
    zoneSupportReward: numRange,
  }),
  isolation: z.strictObject({ holdfastExpansionAdd: nonNegative }),
})
export type AiConfig = z.infer<typeof aiConfigSchema>

/** outlaws.json (M5 spec §9). */
export const outlawsConfigSchema = z
  .strictObject({
    comment,
    clans: z.strictObject({
      capacity: posInt.max(255),
      names: z.array(idString).min(1),
      redTally: nonNegInt,
      convoyWeight: positive,
      harassShareBelow: unit,
      heat: z.strictObject({
        perBandLost: unit,
        perSweep: unit,
        decayPerDay: unit,
        quietAt: unit,
        quietSortieMult: unit,
        quietDays: nonNegInt,
        /** RD-B1 (bug B1): a raid band's last loss calls `onClanBandLost` (`sim.ts` `agentLost`). Absent = off. */
        wireBandLost: z.boolean().optional(),
      }),
      baseDisabledDays: nonNegInt,
      /**
       * RD-B2 (bug B2): where a swept clan looks for a new base. `'region'` (absent, today): its own region only, else
       * it disbands; `'anyRegion'`: when its region has no free node, the nearest free node in any region.
       */
      relocate: z.enum(['region', 'anyRegion']).optional(),
      /**
       * RD-B2 (D-828 U3): a disbanded clan re-forms on the first day ≥ its sweep day + `days` at the free hidden base
       * node with the best unguarded traffic within `raider.targetRange` (`clans.ts` `reformClans`); with no positive
       * score it waits and re-checks every `waitRecheckDays` days (absent = 1). `atOldBase` (arm, absent = off): with no
       * free node near traffic, it re-forms at the fence it never left when traffic runs near that. Absent = never (today).
       */
      reform: z.strictObject({ days: posInt, waitRecheckDays: posInt.optional(), atOldBase: z.boolean().optional() }).optional(),
      growthTreasuryMult: positive,
      fenceTetherstoneMult: positive,
    }),
    guardian: z.strictObject({
      faction: factionIdSchema,
      parent: factionIdSchema,
      ships: intRange,
      aggression: z.strictObject({ keep: unit, extractionMult: nonNegative, instabilityMult: nonNegative, max: positive }),
      extractionDays: posInt,
      sortieS: z.tuple([positive, positive]),
      harassAt: nonNegative,
      harassOutputMult: unit,
      harassDays: nonNegInt,
      holdfastKillStanding: z.number().max(0),
      /** A guardian kill never takes the parent's standing below this (D-335 D4: above the −50 docking refusal). */
      holdfastKillFloor: z.number().min(-100).max(0),
    }),
    naming: z.strictObject({ nameTier: z.number().int().min(0).max(5) }),
  })
  .refine((o) => o.clans.redTally < o.clans.names.length, { message: 'outlaws: clans.redTally must index clans.names' })
  .refine((o) => o.clans.names.length >= o.clans.capacity, { message: 'outlaws: one clan name per clan slot (names ≥ capacity)' })
export type OutlawsConfig = z.infer<typeof outlawsConfigSchema>

const crimeTarget = z.enum(['victim', 'jurisdiction', 'holder', 'owner'])
const crimeBase = { bounty: nonNegative, standing: z.number().max(0), target: crimeTarget }

/** law.json (M5 spec §12). */
export const lawConfigSchema = z
  .strictObject({
    comment,
    crimes: z.strictObject({
      assault: z.strictObject({ ...crimeBase, throttleS: positive, graceS: nonNegative }),
      murder: z.strictObject({ ...crimeBase, hullValueDiv: positive }),
      piracy: z.strictObject({ ...crimeBase, windowS: positive }),
      smuggling: z.strictObject({ ...crimeBase, perTonne: nonNegative }),
      tollEvasion: z.strictObject(crimeBase),
      trespass: z.strictObject(crimeBase),
    }),
    witness: z.strictObject({ rangeM: positive }),
    wanted: z.tuple([positive, positive, positive]).refine(([a, b, c]) => a < b && b < c, { message: 'wanted thresholds must rise' }),
    decay: z.strictObject({ inside: unit, outside: unit }),
    fines: z.strictObject({ faction: factionIdSchema, mult: positive, rangeM: positive }),
    standing: z.strictObject({ hostileAt: signedStanding, contractBonusAt: signedStanding, min: signedStanding, max: signedStanding }),
    scan: z.strictObject({ rangeM: positive, seconds: positive }),
    gains: z.strictObject({ zoneSupport: nonNegative, zoneOpposed: z.number().max(0), warEnemyKill: nonNegative, outlawKill: nonNegative, outlawKillCapPerDay: nonNegative }),
    assaultRing: posInt.refine(isPowerOfTwo, { message: 'expected a power of two' }),
  })
  .refine((l) => CRIME_NAMES.every((c) => c in l.crimes), { message: 'law: every crime needs an entry' })
export type LawConfig = z.infer<typeof lawConfigSchema>

/** votes.json (M5 spec §11). */
export const votesConfigSchema = z.strictObject({
  comment,
  capacity: posInt,
  delayDays: nonNegInt,
  cooldownDays: nonNegInt,
  chair: factionIdSchema,
  chairWeight: nonNegative,
  ballot: z.strictObject({
    threshold: z.number(),
    orderEvidence: z.number(),
    labor: z.number(),
    tetherstone: z.number(),
    greedTetherShare: z.number(),
    sponsorStanding: z.number(),
    publicPressure: z.number(),
    complicity: z.number(),
    exposedFlag: idString,
  }),
  ban: z.strictObject({ ware: wareIdSchema, blackOpsFraction: unit, seizureChance: unit, seizureStanding: z.number().max(0), seizureInfluence: z.number().max(0) }),
  /** M7 (plan S18; spec §8.2, §12.2–§12.3): the player corporation's ballot, the repeal push, Meridian's ban, claim states. */
  story: z.strictObject({
    comment,
    stanceDefault: z.strictObject({ tetherstoneBan: z.enum(STORY_BALLOT_STANCES), tetherstoneRepeal: z.enum(STORY_BALLOT_STANCES) }),
    playerWeightCap: nonNegative,
    repeal: z.strictObject({ sponsor: factionIdSchema, delayOnlineDays: nonNegInt, evidenceWeight: unit }),
    meridianBan: z.strictObject({ sponsor: factionIdSchema, target: factionIdSchema, minPressure: nonNegative, maxOwnTetherShare: unit, evidenceWeight: unit }),
    claimStates: z.strictObject({
      underReviewOpsMult: unit,
      harbouringSecurityMult: unit,
      groups: z.record(idString, z.union([factionIdSchema, z.literal(PLAYER_FACTION_ID)])),
    }),
  }),
})
export type VotesConfig = z.infer<typeof votesConfigSchema>

/** zones.json (M5 spec §8.3). */
export const zonesConfigSchema = z.strictObject({
  comment,
  capacity: posInt,
  openChance: unit,
  durationS: numRange,
  shipsPerSide: intRange,
  roundS: positive,
  winInfluence: nonNegative,
  winPressure: nonNegative,
  hudRangeM: positive,
})
export type ZonesConfig = z.infer<typeof zonesConfigSchema>

// ---------------------------------------------------------------------------------------------
// M5 commands (M5 spec §16). C11 joins charterCommandSchema into the command queue.
// ---------------------------------------------------------------------------------------------

const standingRecord = z.partialRecord(factionIdSchema, signedStanding)

const charterDevCommandSchema = z.discriminatedUnion('op', [
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceRelation'), a: factionIdSchema, b: factionIdSchema, relation: z.enum(RELATION_NAMES) }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceState'), systemId: idString, kind: z.enum(STATE_KIND_NAMES), stage: z.enum(STAGE_NAMES) }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceClaim'), systemId: idString, holder: z.union([factionIdSchema, z.literal('player')]).nullable() }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('runVoteNow') }),
])

export const charterCommandSchema = z.union([
  z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('reportCrime'), kind: z.enum(CRIME_NAMES), victimFaction: factionIdSchema, systemId: idString, value: nonNegative }),
    z.strictObject({ type: z.literal('payFines') }),
    z.strictObject({ type: z.literal('zoneSupport'), zoneId: idString, side: z.enum(['a', 'b']), damage: nonNegative }),
    z.strictObject({ type: z.literal('tollPaid'), relayId: idString, amount: nonNegative }),
    z.strictObject({ type: z.literal('tollEvaded'), relayId: idString }),
    z.strictObject({ type: z.literal('playerContractDone'), factionId: factionIdSchema, systemId: idString, reward: nonNegative }),
    z.strictObject({ type: z.literal('relinkSponsored'), relayId: idString, factionId: factionIdSchema }),
    z.strictObject({ type: z.literal('playerStandings'), standings: standingRecord }),
    z.strictObject({ type: z.literal('playerStandingDelta'), factionId: factionIdSchema, delta: z.number().min(-200).max(200), reason: idString }),
    z.strictObject({ type: z.literal('setStoryTier'), tier: z.number().int().min(0).max(5) }),
    z.strictObject({ type: z.literal('setWorldFlag'), key: idString, value: z.union([z.number(), z.string(), z.boolean()]) }),
    z.strictObject({
      type: z.literal('postWire'),
      item: z.strictObject({
        headline: idString,
        severity: z.number().int().min(1).max(3),
        systemId: idString.optional(),
        source: z.enum(STORY_WIRE_OUTLETS).optional(),
      }),
    }),
    z.strictObject({
      type: z.literal('proposeCharterVote'),
      measure: z.enum(MEASURE_NAMES),
      sponsor: z.union([factionIdSchema, z.literal('player')]),
      evidenceWeight: unit,
    }),
  ]),
  charterDevCommandSchema,
]) satisfies z.ZodType<CharterCommand>

// ---------------------------------------------------------------------------------------------
// M5 save v2 (M5 spec §17). C11 makes it the parsed version with the real v1 → v2 migration.
// ---------------------------------------------------------------------------------------------

const b64Cols = z.record(idString, base64Schema)

export const charterVoteSchema = z.strictObject({
  id: idString,
  measure: z.enum(MEASURE_NAMES),
  proposedDay: nonNegInt,
  voteDay: nonNegInt,
  sponsor: z.union([factionIdSchema, z.literal('player')]),
  evidenceWeight: unit,
  ballots: z.array(z.strictObject({ faction: factionIdSchema, weight: nonNegative, yes: z.boolean() })),
  result: z.enum(['pending', 'passed', 'failed']),
}) satisfies z.ZodType<CharterVote>

export const charterSaveSchema = z.strictObject({
  standings: standingRecord,
  relations: b64Cols,
  factionAi: b64Cols,
  claims: z.strictObject({ trendHead: nonNegInt, trendFilled: nonNegInt, cols: b64Cols }),
  states: b64Cols,
  stations: b64Cols,
  zones: z.array(
    z.strictObject({
      id: idString,
      systemId: idString,
      node: z.number().int(),
      sideA: factionIdSchema,
      sideB: factionIdSchema,
      opened: nonNegative,
      until: nonNegative,
      strengthA: nonNegative,
      strengthB: nonNegative,
      supportA: nonNegative,
      supportB: nonNegative,
      slot: nonNegInt.optional(),
    }),
  ),
  law: z.strictObject({
    bounties: z.partialRecord(factionIdSchema, nonNegative),
    lastCrimeDay: z.partialRecord(factionIdSchema, z.number().int()),
    murderedWrecks: z.array(z.tuple([idString, nonNegative])),
    guardianGrudge: z.boolean(),
  }),
  votes: z.strictObject({ list: z.array(charterVoteSchema), lastProposedDay: z.number().int(), nextSeq: nonNegInt }),
  illegal: z.array(wareIdSchema),
  gateStage: z.strictObject({
    stage: z.enum(GATE_STAGE_NAMES),
    closedUntil: z.number(),
    reopenStreak: nonNegInt,
    isolation: z.enum(STAGE_NAMES),
    isolationDaysLeft: z.number(),
  }),
  storyTier: z.number().int().min(0).max(5),
  flags: z.array(z.tuple([idString, z.union([z.number(), z.string(), z.boolean()])])),
  clans: z.array(
    z.strictObject({
      baseNode: z.number().int(),
      active: z.boolean().optional(),
      systemId: idString,
      treasury: z.number(),
      aggression: nonNegative,
      heat: unit,
      bands: nonNegInt,
      quietUntilDay: z.number().int(),
      baseDisabledUntilDay: z.number().int(),
      nameIndex: nonNegInt,
    }),
  ),
  guardian: z.strictObject({ baseNode: z.number().int(), aggression: nonNegative, nextSortieT: z.number(), extraction: z.array(nonNegative) }),
  requests: z.strictObject({ list: z.array(factionRequestSchema), nextSeq: nonNegInt }),
  zoneSeq: nonNegInt,
}) satisfies z.ZodType<CharterSave>

export const strategicSaveV2Schema = strategicSaveV1Schema.extend({
  version: z.literal(2),
  charter: charterSaveSchema,
}) satisfies z.ZodType<StrategicSaveV2>


// =============================================================================================
// M6 (M6 spec; plan P0): fleet.json, orders.json, captains.json, corp.json, commission.json and wire-m6.json, the
// M6 commands and the fleet part of the v3 save. References across files are checked by data.ts.
// =============================================================================================

const shipClassSchema = z.enum(SHIP_CLASS_NAMES)
const orderParamSchema = z.enum(ORDER_PARAM_NAMES)
const signedRange = z.tuple([z.number(), z.number()]).refine(([lo, hi]) => lo <= hi, { message: 'expected [min, max] with min ≤ max' })
const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i, { message: 'expected a #rrggbb colour' })
const tickerSchema = z.string().regex(/^[A-Z]{3,4}$/, { message: 'expected 3-4 capital letters' })

/**
 * The roles.json field each role-row param (ORDER_PARAM 0 … ROLE_PARAM_COUNT − 1) comes from, per role an order can
 * run as (plan P3 compiles an order's row from it; params a role lacks keep orders.json defaults or 0).
 */
export const ROLE_PARAM_FIELDS = {
  trader: { jumpRange: 'jumpRange', minMargin: 'minMargin', riskWeight: 'riskWeight', maxRisk: 'maxRisk', minLot: 'minLot' },
  miner: { homeRange: 'homeRange', sellRange: 'sellRange', riskWeight: 'riskWeight' },
  supplier: { jumpRange: 'jumpRange', restockMargin: 'restockMargin', minDeficit: 'minDeficit' },
  patrol: { respondRange: 'respondRange' },
} as const satisfies Record<string, Partial<Record<OrderParamName, string>>>
export type OrderRole = keyof typeof ROLE_PARAM_FIELDS
export const ORDER_ROLES = ['trader', 'miner', 'supplier', 'patrol'] as const satisfies readonly OrderRole[]

/** True for the role-row params (their defaults come from roles.json). */
export function isRoleParam(name: OrderParamName): boolean {
  return ORDER_PARAM[name] < ROLE_PARAM_COUNT
}

// fleet.json (M6 spec §4, §6.2)

export const SHIP_UTILITY = ['miningLaser', 'collector', 'cuttingBeam', 'heavyTractor'] as const
const classDefSchema = z.strictObject({
  role: idString,
  crew: intRange.refine(([lo]) => lo >= 1, { message: 'a ship needs a crew' }),
  hold: numRange,
  weapons: nonNegInt,
  turrets: nonNegInt,
  utility: z.array(z.enum(SHIP_UTILITY)),
  price: numRange.refine(([lo]) => lo > 0, { message: 'a price is positive' }),
  wingBonus: z.number().min(0).max(0.25),
})
export type ShipClassDef = z.infer<typeof classDefSchema>

/**
 * M9 (CH-1b, D-TBD M9-5): disabled combat losses and open claims. `disabledShare` of the off-screen losses of an AI
 * faction or raider ship whose hull sells as one of `eligibleClasses` (small/medium; never the player's or a
 * corporation's ship, never a guardian; H-3) is left disabled (`WRECK_CLAIM.disabled`), rolled by a hash of the
 * wreck seed (MS-2). A disabled record lives `lifetimeDays` faction days (H-4); an open claim (at most `maxOpen`,
 * MS-1) is exempt from the cap and the expiry. `disabledShare` 0 turns the off-screen source off.
 */
const claimConfigSchema = z.strictObject({
  disabledShare: unit,
  lifetimeDays: positive,
  /** Far below the 512-record cap, so eviction always finds a record that is not an open claim. */
  maxOpen: z.number().int().min(0).max(16),
  eligibleClasses: z.array(shipClassSchema).refine((a) => new Set(a).size === a.length, { message: 'eligibleClasses repeats a class' }),
  /** M9 (CH-7a, MS-3): the claimed ship's first ferry home flies under a generic transfer crew with this ROE (no captain). */
  ferry: z.strictObject({ roe: z.enum(ROE_NAMES) }).default({ roe: 'evade' }),
  /**
   * M9 (CH-9b): a flawed fleet ship's off-screen combat strength × this per flaw (`combat/encounters.ts combatStrength`).
   * The worker's mirror of `hulks.json combat.strengthMultPerFlaw` (the worker doesn't import the hulk tables).
   */
  strengthMultPerFlaw: z.number().gt(0).max(1).default(0.95),
})
export type ClaimConfig = z.infer<typeof claimConfigSchema>
/** The `claim` block an older fleet.json (without one) parses to: fleet.json's values (plan §1 H-3, H-4, MS-1). */
const CLAIM_CONFIG_DEFAULT: ClaimConfig = {
  disabledShare: 0.1,
  lifetimeDays: 10,
  maxOpen: 3,
  eligibleClasses: ['light_fighter', 'interceptor', 'gunship', 'miner', 'salvager', 'hauler', 'survey'],
  ferry: { roe: 'evade' },
  strengthMultPerFlaw: 0.95,
}

export const fleetConfigSchema = z.strictObject({
  comment,
  classes: z.record(shipClassSchema, classDefSchema),
  wing: z
    .strictObject({ size: posInt, corvetteSize: posInt, followRelayM: positive })
    .refine((w) => w.corvetteSize >= w.size, { message: 'wing.corvetteSize must be at least wing.size' }),
  towing: z.strictObject({ maxPartT: positive }),
  insurance: z.strictObject({
    requiresLeaseOwned: z.boolean(),
    dailyRate: unit,
    minPerDay: nonNegative,
    rebuildBase: nonNegative,
    moduleValueShare: unit,
    insuredFeeMult: unit,
  }),
  transfer: z.strictObject({ feePerJump: nonNegative, minFee: nonNegative, jumpsPerDay: posInt }),
  soleTrader: z.strictObject({ maxExtraShips: nonNegInt }),
  names: z.strictObject({ first: z.array(idString).min(1), second: z.array(idString).min(1) }),
  /** M9 (CH-1b): absent (an older fleet.json) = `CLAIM_CONFIG_DEFAULT`. */
  claim: claimConfigSchema.default(() => ({ ...CLAIM_CONFIG_DEFAULT, eligibleClasses: [...CLAIM_CONFIG_DEFAULT.eligibleClasses], ferry: { ...CLAIM_CONFIG_DEFAULT.ferry } })),
})
export type FleetConfig = z.infer<typeof fleetConfigSchema>

// orders.json (M6 spec §6.1)

const paramOverrides = z.partialRecord(orderParamSchema, z.number())
const orderDefSchema = z.strictObject({
  role: z.enum(ORDER_ROLES),
  roe: z.enum(ROE_NAMES),
  classes: z.array(shipClassSchema).min(1),
  sellPolicy: z.enum(SELL_POLICY_NAMES).optional(),
  patrolRule: z.enum(PATROL_RULE_NAMES).optional(),
  params: paramOverrides,
})
export type OrderDef = z.infer<typeof orderDefSchema>
const presetSchema = z.strictObject({
  id: idString,
  name: idString,
  order: z.enum(ORDER_NAMES),
  roe: z.enum(ROE_NAMES),
  stops: z.array(z.enum(STOP_NAMES)),
  params: paramOverrides,
  sellPolicy: z.enum(SELL_POLICY_NAMES).optional(),
  patrolRule: z.enum(PATROL_RULE_NAMES).optional(),
  wares: z.array(wareIdSchema).optional(),
  blockWares: z.array(wareIdSchema),
})
export type OrderPreset = z.infer<typeof presetSchema>

/** Role-row params an order sets must exist in its role's roles.json row. */
function roleParamsOk(role: OrderRole, params: Partial<Record<OrderParamName, number>>): string | null {
  const fields: Partial<Record<OrderParamName, string>> = ROLE_PARAM_FIELDS[role]
  for (const k of Object.keys(params) as OrderParamName[]) {
    if (isRoleParam(k) && fields[k] === undefined) return `sets ${k}, which the ${role} role has no row for`
  }
  return null
}

export const ordersConfigSchema = z
  .strictObject({
    comment,
    orders: z.record(z.enum(ORDER_NAMES), orderDefSchema),
    presets: z.array(presetSchema).min(1),
    defaultOrder: z.enum(ORDER_NAMES),
    limits: z.partialRecord(orderParamSchema, signedRange),
  })
  .superRefine((file, ctx) => {
    const within = (where: string, params: Partial<Record<OrderParamName, number>>): void => {
      for (const [k, v] of Object.entries(params) as [OrderParamName, number][]) {
        const lim = file.limits[k]
        if (lim !== undefined && (v < lim[0] || v > lim[1])) ctx.addIssue({ code: 'custom', message: `${where}: ${k} ${v} outside limits [${lim[0]}, ${lim[1]}]` })
      }
    }
    for (const [name, def] of Object.entries(file.orders)) {
      const bad = roleParamsOk(def.role, def.params)
      if (bad !== null) ctx.addIssue({ code: 'custom', message: `orders.${name} ${bad}` })
      within(`orders.${name}`, def.params)
    }
    const ids = new Set<string>()
    for (const p of file.presets) {
      if (ids.has(p.id)) ctx.addIssue({ code: 'custom', message: `duplicate preset id ${p.id}` })
      ids.add(p.id)
      const bad = roleParamsOk(file.orders[p.order].role, p.params)
      if (bad !== null) ctx.addIssue({ code: 'custom', message: `preset ${p.id} ${bad}` })
      within(`preset ${p.id}`, p.params)
    }
  })
export type OrdersConfig = z.infer<typeof ordersConfigSchema>

// captains.json (M6 spec §5)

const skillPerLevel = z.partialRecord(z.enum(['escape', 'survival', 'margin', 'route', 'yield', 'odds', 'wing', 'station']), unit)
export const captainsConfigSchema = z
  .strictObject({
    comment,
    capacity: z.strictObject({ hired: posInt.max(32767) }),
    memorial: posInt,
    portraits: posInt.max(256),
    skills: z.strictObject({ min: posInt, max: posInt.max(255), neutralLevel: posInt }),
    skillEffects: z.record(z.enum(SKILL_NAMES), skillPerLevel),
    traits: z.strictObject({
      cautious: z.strictObject({ profit: z.number(), risk: z.number() }),
      reckless: z.strictObject({ profit: z.number(), risk: z.number() }),
      greedy: z.strictObject({ margin: z.number(), skim: unit }),
      veteran: z.strictObject({ combatLevels: nonNegInt, escape: unit }),
      exDescendant: z.strictObject({ standing: z.number(), revealPoi: z.boolean() }),
      exMegacorp: z.strictObject({ megacorpPrice: unit, guardiansHostile: z.boolean() }),
      unionist: z.strictObject({ strikes: z.boolean() }),
      loyal: z.strictObject({ decayMult: unit, neverDeserts: z.boolean() }),
    }),
    bounds: z.strictObject({ modifier: unit }),
    traitCount: intRange,
    traitWeights: z.record(z.enum(CAPTAIN_TRAIT_NAMES), nonNegative),
    wage: z.strictObject({ base: positive, perSkillPoint: nonNegative, jitter: unit, greedyMult: positive }),
    loyalty: z.strictObject({
      hire: numRange,
      drift: z.strictObject({ target: z.number().min(0).max(100), perDay: nonNegative }),
      latePerDay: nonNegative,
      shipLost: nonNegative,
      paidPerDay: nonNegative,
      quitAt: z.number().min(0).max(100),
      max: z.number().min(0).max(100),
    }),
    strike: z.strictObject({ underpaidBelow: unit, days: posInt }),
    desertion: z.strictObject({ traits: z.array(z.enum(CAPTAIN_TRAIT_NAMES)), chance: unit }),
    xp: z.strictObject({ run: nonNegative, fight: nonNegative, thresholds: z.array(positive).min(1) }),
    survival: z.strictObject({ base: unit, escapePod: unit, perPiloting: unit, max: unit, recoverDays: posInt }),
    roster: z.strictObject({ size: intRange.refine(([lo]) => lo >= 1, { message: 'a roster has at least one candidate' }), refreshDays: posInt, skillWeights: z.array(nonNegative).min(1) }),
    flavour: z.partialRecord(factionIdSchema, z.partialRecord(z.enum(CAPTAIN_TRAIT_NAMES), positive)),
    combatProfiles: z.array(z.enum(['rookie', 'veteran', 'ace'])).min(1),
    names: z.strictObject({ given: z.array(idString).min(1), family: z.array(idString).min(1) }),
  })
  .superRefine((c, ctx) => {
    const levels = c.skills.max - c.skills.min + 1
    if (c.skills.min > c.skills.max) ctx.addIssue({ code: 'custom', message: 'skills.min must not exceed skills.max' })
    if (c.skills.neutralLevel < c.skills.min || c.skills.neutralLevel > c.skills.max) ctx.addIssue({ code: 'custom', message: 'skills.neutralLevel must be a skill level' })
    if (c.xp.thresholds.length !== levels - 1) ctx.addIssue({ code: 'custom', message: `xp.thresholds needs ${levels - 1} entries (one per level up)` })
    for (let i = 1; i < c.xp.thresholds.length; i++) {
      if (c.xp.thresholds[i] <= c.xp.thresholds[i - 1]) ctx.addIssue({ code: 'custom', message: 'xp.thresholds must increase' })
    }
    if (c.roster.skillWeights.length !== levels) ctx.addIssue({ code: 'custom', message: `roster.skillWeights needs ${levels} entries` })
    if (c.combatProfiles.length !== levels) ctx.addIssue({ code: 'custom', message: `combatProfiles needs ${levels} entries` })
    if (c.traitCount[1] > 2 || c.traitCount[0] < 1) ctx.addIssue({ code: 'custom', message: 'traitCount must be within [1, 2] (M6 spec §5)' })
    if (c.loyalty.hire[1] > c.loyalty.max) ctx.addIssue({ code: 'custom', message: 'loyalty.hire must not exceed loyalty.max' })
    if (c.survival.base + c.survival.escapePod > 1) ctx.addIssue({ code: 'custom', message: 'survival.base + escapePod must not exceed 1' })
    const b = c.bounds.modifier
    for (const t of ['cautious', 'reckless'] as const) {
      if (Math.abs(c.traits[t].profit) > b) ctx.addIssue({ code: 'custom', message: `traits.${t}.profit exceeds bounds.modifier` })
    }
    if (Math.abs(c.traits.greedy.margin) > b) ctx.addIssue({ code: 'custom', message: 'traits.greedy.margin exceeds bounds.modifier' })
  })
export type CaptainsConfig = z.infer<typeof captainsConfigSchema>

// corp.json (M6 spec §7.1, §7.3)

export const liveryColorsSchema = z.strictObject({ primary: hexColor, secondary: hexColor, accent: hexColor }) satisfies z.ZodType<LiveryColors>
export const logoRecipeSchema = z.strictObject({
  seed: z.number().int().nonnegative(),
  draft: nonNegInt,
  shape: nonNegInt,
  palette: nonNegInt,
  finishing: z.array(idString).max(64),
}) satisfies z.ZodType<LogoRecipe>

export const corpConfigSchema = z
  .strictObject({
    comment,
    slot: z.strictObject({ index: nonNegInt.max(253), teamId: z.literal(0), livery: idString }),
    founding: z.strictObject({ fee: nonNegative, requiresOwnership: z.boolean(), registrationTemplate: idString, tickerLength: intRange }),
    tickers: z.record(factionIdSchema, tickerSchema),
    livery: liveryColorsSchema,
    logo: z.strictObject({ drafts: posInt, shapes: posInt, palettes: posInt }),
    standings: z.strictObject({ default: signedStanding, factions: z.partialRecord(factionIdSchema, signedStanding) }),
    transfers: z.strictObject({ minAmount: nonNegative }),
    claims: z.strictObject({
      tariff: unitRange,
      docking: z.enum(DOCKING_RIGHTS_NAMES),
      securityBands: z.strictObject({ medium: nonNegative, high: nonNegative }),
    }),
    milestones: z.strictObject({ fiveClaims: posInt, regionDominance: z.strictObject({ share: unit, minSystems: posInt }) }),
    rivals: z.strictObject({ threatAt: unit, sabotageRaidMult: positive, sabotageDays: posInt }),
  })
  .superRefine((c, ctx) => {
    const seen = new Set<string>()
    for (const [id, t] of Object.entries(c.tickers)) {
      if (seen.has(t)) ctx.addIssue({ code: 'custom', message: `tickers: "${t}" (${id}) is not unique` })
      seen.add(t)
    }
    if (c.founding.tickerLength[0] < 3 || c.founding.tickerLength[1] > 4) ctx.addIssue({ code: 'custom', message: 'founding.tickerLength must be within [3, 4]' })
    if (c.claims.tariff[1] > 0.1) ctx.addIssue({ code: 'custom', message: 'claims.tariff must stay within 0-10% (M6 spec §7.3)' })
    if (c.claims.securityBands.high < c.claims.securityBands.medium) ctx.addIssue({ code: 'custom', message: 'claims.securityBands: high must be ≥ medium' })
  })
export type CorpConfig = z.infer<typeof corpConfigSchema>

// commission.json (M6 spec §7.2)

const perLevel = z.tuple([positive, positive, positive])
export const commissionConfigSchema = z.strictObject({
  comment,
  charter: z.strictObject({ price: nonNegative, minStanding: signedStanding }),
  sites: z.strictObject({ maxPerSystem: posInt }),
  types: z.record(
    z.enum(STATION_TYPE_NAMES),
    z.strictObject({
      buildDays: perLevel.refine((d) => d[0] <= d[1] && d[1] <= d[2], { message: 'buildDays must not decrease with the level' }),
      security: z.tuple([nonNegative, nonNegative, nonNegative]).optional(),
    }),
  ),
  autoBuy: z.boolean(),
  pricePolicy: z.enum(PRICE_POLICY_NAMES),
  permission: z.enum(TRADE_PERMISSION_NAMES),
  manager: z.strictObject({ perTrade: unit, perLeadership: unit }),
})
export type CommissionConfig = z.infer<typeof commissionConfigSchema>

// wire-m6.json (M6 spec §5, §8)

/** Slots of M6 Wire items and captain log lines. */
export const M6_WIRE_SLOTS = [
  'corp',
  'ticker',
  'captain',
  'ship',
  'class',
  'system',
  'station',
  'ware',
  'amount',
  'faction',
  'order',
  'skill',
  'level',
  'type',
  'milestone',
  'days',
] as const
/** The corporate Wire items P13 posts (each needs templates and a severity). */
export const M6_WIRE_ITEM_KEYS = [
  'corpFounded',
  'stationCommissioned',
  'commissionBuilt',
  'stationUpgraded',
  'corpClaimTaken',
  'corpClaimLost',
  'captainDied',
  'captainDeserted',
  'milestone',
] as const
/** The M6 event names (EV codes from `shipBought` on): each needs captain log lines. */
export const M6_EVENT_NAMES = (Object.keys(EV) as (keyof typeof EV)[]).filter((k) => EV[k] >= EV.shipBought && EV[k] <= EV.insurancePaid)

const m6Variants = z.array(templateText(M6_WIRE_SLOTS)).min(1).max(4)
export const wireM6ConfigSchema = z
  .strictObject({
    comment,
    feedKeep: posInt,
    severity: z.record(idString, WIRE_SEVERITY),
    /** Keyed by item; P13 may add keys (the required ones are M6_WIRE_ITEM_KEYS). */
    items: z.record(idString, m6Variants),
    /** Keyed by M6 event name; P10 may add keys (every M6 event needs one). */
    logs: z.record(idString, m6Variants),
    milestones: z.record(z.enum(MILESTONE_NAMES), idString),
  })
  .superRefine((w, ctx) => {
    for (const k of M6_WIRE_ITEM_KEYS) {
      if (w.items[k] === undefined) ctx.addIssue({ code: 'custom', message: `items.${k} is missing` })
      if (w.severity[k] === undefined) ctx.addIssue({ code: 'custom', message: `severity.${k} is missing` })
    }
    for (const k of Object.keys(w.severity)) if (w.items[k] === undefined) ctx.addIssue({ code: 'custom', message: `severity.${k} names no item` })
    for (const k of M6_EVENT_NAMES) if (w.logs[k] === undefined) ctx.addIssue({ code: 'custom', message: `logs.${k} is missing` })
    const evNames = new Set<string>(M6_EVENT_NAMES)
    for (const k of Object.keys(w.logs)) if (!evNames.has(k)) ctx.addIssue({ code: 'custom', message: `logs.${k} is not an M6 event` })
  })
export type WireM6Config = z.infer<typeof wireM6ConfigSchema>

// ---------------------------------------------------------------------------------------------
// M6 commands (M6 spec §4-§7). P15 joins m6CommandSchema into the command queue.
// ---------------------------------------------------------------------------------------------

const credits = z.number().finite()
const priceLimit = z.strictObject({ ware: wareIdSchema, min: nonNegative.nullable(), max: nonNegative.nullable() })

export const standingOrderSchema = z.strictObject({
  order: z.enum(ORDER_NAMES),
  roe: z.enum(ROE_NAMES),
  stops: z.array(z.enum(STOP_NAMES)).max(STOP_NAMES.length),
  params: z.partialRecord(orderParamSchema, z.number().finite()),
  wares: z.array(wareIdSchema),
  blockWares: z.array(wareIdSchema),
  systems: z.array(idString),
  sellPolicy: z.enum(SELL_POLICY_NAMES).optional(),
  sellStation: idString.optional(),
  target: idString.optional(),
  priority: z.array(wareIdSchema).max(4).optional(),
  patrolRule: z.enum(PATROL_RULE_NAMES).optional(),
  laneRelay: idString.optional(),
  escort: idString.optional(),
  stopDay: nonNegInt.optional(),
}) satisfies z.ZodType<StandingOrder>

const m6DevCommandSchema = z.discriminatedUnion('op', [
  z.strictObject({ type: z.literal('dev'), op: z.literal('grantShip'), hull: idString, stationId: idString }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('grantCredits'), amount: credits }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceFounding') }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceShipLoss'), shipId: idString }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceDesertion'), captainId: idString }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceCaptainDeath'), captainId: idString }),
  z.strictObject({ type: z.literal('dev'), op: z.literal('finishCommission'), commissionId: idString }),
  // M9 CH-12d: a disabled off-screen wreck for the dev panel and the e2e.
  z.strictObject({ type: z.literal('dev'), op: z.literal('forceDisabledLoss'), systemId: idString, hull: idString.optional() }),
])

export const m6CommandSchema = z.union([
  z.discriminatedUnion('type', [
    z.strictObject({
      type: z.literal('buyShip'),
      yardId: idString,
      hull: idString,
      standing: signedStanding,
      payFrom: z.enum(['company', 'wallet']),
      wallet: credits,
      // M10 P5a3b: the player's name for the new ship (absent = the default); `buyShip` checks the length and the trim.
      name: idString.optional(),
    }),
    // M10 P5a3b: the player renames an owned ship (`renameShip` checks the length, the trim and uniqueness).
    z.strictObject({ type: z.literal('renameShip'), shipId: idString, name: idString }),
    z.strictObject({ type: z.literal('setOrder'), shipId: idString, order: standingOrderSchema }),
    z.strictObject({ type: z.literal('applyPreset'), shipIds: z.array(idString).min(1), preset: idString }),
    z.strictObject({ type: z.literal('setInsurance'), shipId: idString, insured: z.boolean(), leaseOwned: z.boolean() }),
    z.strictObject({ type: z.literal('ferryShip'), shipId: idString, stationId: idString }),
    z.strictObject({ type: z.literal('transferService'), shipId: idString, stationId: idString, wallet: credits }),
    z.strictObject({ type: z.literal('boardShip'), shipId: idString }),
    z.strictObject({ type: z.literal('leaveShip'), stationId: idString }),
    z.strictObject({ type: z.literal('loseBoardedShip'), shipId: idString }),
    z.strictObject({ type: z.literal('hireCaptain'), stationId: idString, candidate: nonNegInt, wallet: credits }),
    z.strictObject({ type: z.literal('fireCaptain'), captainId: idString }),
    z.strictObject({ type: z.literal('assignCaptain'), captainId: idString, shipId: idString.nullable(), stationId: idString.nullable() }),
    z.strictObject({ type: z.literal('payWages'), captainId: idString.nullable(), wallet: credits }),
    z.strictObject({
      type: z.literal('foundCorp'),
      name: z.string().min(1).max(40),
      ticker: tickerSchema,
      livery: liveryColorsSchema,
      logo: logoRecipeSchema,
      leaseOwned: z.boolean(),
      wallet: credits,
    }),
    z.strictObject({ type: z.literal('setLivery'), livery: liveryColorsSchema }),
    z.strictObject({ type: z.literal('companyTransfer'), amount: positive, direction: z.enum(['toCompany', 'toWallet']), wallet: credits }),
    z.strictObject({ type: z.literal('buyCharter'), systemId: idString, standing: signedStanding, wallet: credits }),
    z.strictObject({
      type: z.literal('commissionStation'),
      systemId: idString,
      site: nonNegInt,
      stationType: z.enum(STATION_TYPE_NAMES),
      level: z.number().int().min(1).max(3),
      autoBuy: z.boolean(),
    }),
    z.strictObject({ type: z.literal('upgradeStation'), commissionId: idString }),
    z.strictObject({ type: z.literal('setPricePolicy'), commissionId: idString, policy: z.enum(PRICE_POLICY_NAMES), limits: z.array(priceLimit) }),
    z.strictObject({ type: z.literal('setPermissions'), commissionId: idString, permission: z.enum(TRADE_PERMISSION_NAMES) }),
    z.strictObject({ type: z.literal('setManager'), commissionId: idString, captainId: idString.nullable() }),
    z.strictObject({ type: z.literal('setClaimTariff'), systemId: idString, tariff: z.number().min(0).max(0.1).nullable() }),
    z.strictObject({ type: z.literal('setDockingRights'), systemId: idString, rights: z.enum(DOCKING_RIGHTS_NAMES) }),
  ]),
  m6DevCommandSchema,
]) satisfies z.ZodType<M6Command>

/**
 * M10 P5a10w (review F1): the cradle pair's player commands (`commands.ts` `launchFromCarrier`, `dockToCarrier`). A
 * null id is the Career's own ship (not a fleet ship); the two ids differ. `stationId` on a launch puts the carried
 * ship back in the pool docked there (the inverse of stowing it at a station). Player commands only: no AI path.
 */
export type CarrierCommand =
  | { type: 'launchFromCarrier'; carrierId: string | null; shipId: string | null; stationId?: string }
  | { type: 'dockToCarrier'; carrierId: string | null; shipId: string | null }

export const carrierCommandSchema = z
  .discriminatedUnion('type', [
    z.strictObject({ type: z.literal('launchFromCarrier'), carrierId: idString.nullable(), shipId: idString.nullable(), stationId: idString.optional() }),
    z.strictObject({ type: z.literal('dockToCarrier'), carrierId: idString.nullable(), shipId: idString.nullable() }),
  ])
  .refine((c) => c.carrierId !== c.shipId, { message: 'the carrier and the carried ship must differ' }) satisfies z.ZodType<CarrierCommand>

// ---------------------------------------------------------------------------------------------
// M6 save: the fleet part of v3 (P1 builds strategicSaveV3Schema on it in save.ts; P16 fills it)
// ---------------------------------------------------------------------------------------------

const corpFactionIdSchema = z.union([factionIdSchema, z.literal('player')])

export const memorialEntrySchema = z.strictObject({
  captainId: idString,
  name: idString,
  portrait: nonNegInt,
  shipName: idString,
  shipClass: shipClassSchema,
  systemId: idString,
  day: nonNegInt,
  killer: corpFactionIdSchema.nullable(),
  runs: nonNegInt,
  kills: nonNegInt,
  daysServed: nonNegInt,
}) satisfies z.ZodType<MemorialEntry>

export const fleetLogEntrySchema = z.strictObject({
  id: nonNegInt,
  t: nonNegative,
  day: nonNegInt,
  type: nonNegInt,
  shipId: idString.nullable(),
  captainId: idString.nullable(),
  systemId: idString.nullable(),
  text: idString,
}) satisfies z.ZodType<FleetLogEntry>

export const fleetSaveSchema = z.strictObject({
  fleet: z.strictObject({ high: nonNegInt, nextSeq: nonNegInt, names: z.array(z.string()), cols: b64Cols }),
  captains: z.strictObject({ hiredCount: nonNegInt, nextSeq: nonNegInt, cols: b64Cols }),
  corp: z.strictObject({
    faction: z.number().int().min(-1),
    founded: z.boolean(),
    charterEligible: z.boolean(),
    name: z.string(),
    ticker: z.string(),
    livery: liveryColorsSchema,
    logo: logoRecipeSchema.nullable(),
    foundedDay: z.number().int(),
    milestones: nonNegInt,
    milestoneDay: z.array(z.number().int()),
    pendingWallet: z.number(),
    shipsEver: nonNegInt,
    cols: b64Cols,
  }),
  commissions: z.strictObject({ high: nonNegInt, nextSeq: nonNegInt, cols: b64Cols }),
  memorial: z.strictObject({ entries: z.array(memorialEntrySchema), total: nonNegInt }),
  feed: z.strictObject({ entries: z.array(fleetLogEntrySchema), nextId: nonNegInt }),
}) satisfies z.ZodType<FleetSave>

// =============================================================================================
// M7 (plan §2, S16): the story commands and the v4 story block. The command shapes are exactly
// `src/story/schemas.ts` `storyCommandSchema`'s for the same types (`m7.test.ts` checks both accept and refuse alike).
// =============================================================================================

const storyDay = nonNegInt
const storyParty = z.union([factionIdSchema, z.literal('player')])

export const m7CommandSchema = z.union([
  z.strictObject({ type: z.literal('setStoryClock'), storyMult: z.number().positive().max(10) }),
  z.strictObject({ type: z.literal('storyTierDrop'), tier: z.number().int().min(2).max(5), drop: z.number().positive().max(100), cause: z.string() }),
  z.strictObject({ type: z.literal('seedPressure'), a: storyParty, b: storyParty, amount: z.number() }),
  z.strictObject({ type: z.literal('setGrievance'), a: storyParty, b: storyParty, floor: z.number(), untilOnlineDay: storyDay }),
  z.strictObject({ type: z.literal('setAggressionFloor'), faction: factionIdSchema, value: z.number().min(0).max(100), untilOnlineDay: storyDay }),
  z.strictObject({ type: z.literal('setRetaliation'), faction: factionIdSchema, rows: z.array(z.enum(STORY_RETALIATION_ROWS)) }),
  z.strictObject({ type: z.literal('setPlayerBallot'), measure: z.enum(MEASURE_NAMES), stance: z.enum(STORY_BALLOT_STANCES) }),
  z.strictObject({
    type: z.literal('setClaimState'),
    scope: z.enum(['claim', 'commission']),
    id: idString,
    state: z.enum(STORY_CLAIM_STATES),
    untilOnlineDay: storyDay.nullable(),
  }),
  z.strictObject({
    type: z.literal('spawnStoryShip'),
    key: idString,
    role: z.enum(STORY_SHIP_ROLES),
    systemId: idString,
    missionId: z.enum(STORY_MISSION_IDS).optional(),
  }),
  z.strictObject({ type: z.literal('hiddenTransfer'), from: storyParty, to: factionIdSchema, amount: nonNegative }),
  z.strictObject({ type: z.literal('seedBounty'), faction: factionIdSchema, amount: nonNegative, reason: idString }),
  z.strictObject({ type: z.literal('wipeBounties'), faction: factionIdSchema, olderThanDay: storyDay, cap: nonNegative }),
  z.strictObject({ type: z.literal('restoreBounties'), faction: factionIdSchema, amount: nonNegative }),
  z.strictObject({ type: z.literal('registerHoldfastClaims'), registered: z.boolean() }),
  z.strictObject({ type: z.literal('setCharterWaiver'), charters: nonNegInt, waiveMinStanding: z.boolean() }),
  z.strictObject({ type: z.literal('restoreDerelict'), poiId: idString, systemId: idString, template: idString, owner: storyParty }),
  z.strictObject({
    type: z.literal('registerReactivation'),
    poiId: idString,
    registrant: z.enum(STORY_REGISTRANTS),
    recipient: factionIdSchema.optional(),
    fee: nonNegative,
    marker: z.boolean().optional(),
    withFamily: z.boolean().optional(),
    waitOnlineDays: nonNegInt.optional(),
    reapply: z.boolean().optional(),
  }),
  z.strictObject({ type: z.literal('settleRegistryChallenge'), poiId: idString, fee: nonNegative }),
  z.strictObject({ type: z.literal('supplyStation'), poiId: idString, good: idString, qty: nonNegInt }),
  z.strictObject({ type: z.literal('lowerVoteWeight'), measure: z.enum(MEASURE_NAMES), drop: z.number().positive().max(1) }),
  z.strictObject({ type: z.literal('storyDev'), op: z.literal('setOnlineDay'), day: storyDay }),
  z.strictObject({ type: z.literal('storyDev'), op: z.literal('clearStory') }),
]) satisfies z.ZodType<M7Command>

const storyRestoredSchema = z.strictObject({
  poiId: idString,
  systemId: idString,
  template: idString,
  owner: storyParty,
  station: z.number().int().min(-1),
  registrant: z.enum(STORY_REGISTRANTS).nullable(),
  recipient: factionIdSchema.optional(),
  day: storyDay,
  darkDays: nonNegInt,
  dark: z.boolean(),
  marker: z.boolean().optional(),
  withFamily: z.boolean().optional(),
  familyPaid: z.number().optional(),
  pending: z
    .strictObject({
      registrant: z.enum(STORY_REGISTRANTS),
      recipient: factionIdSchema.optional(),
      fee: nonNegative,
      marker: z.boolean().optional(),
      withFamily: z.boolean().optional(),
      dueOnlineDay: storyDay,
    })
    .optional(),
})

/** The v4 M7 block (`StrategicSaveV4.story`). */
export const storySaveSchema = z.strictObject({
  onlineDay: storyDay,
  grievances: z.array(z.strictObject({ a: storyParty, b: storyParty, floor: z.number(), untilOnlineDay: storyDay })),
  guardianFloor: z.strictObject({ value: z.number().min(0).max(100), untilOnlineDay: z.number().int().min(-1) }),
  ballot: z.partialRecord(z.enum(MEASURE_NAMES), z.enum(STORY_BALLOT_STANCES)),
  repeal: z.strictObject({ dueOnlineDay: z.number().int().min(-1), banDay: z.number().int().min(-1) }),
  retaliation: z.partialRecord(factionIdSchema, z.array(z.enum(STORY_RETALIATION_ROWS))),
  restored: z.array(storyRestoredSchema),
  titles: z.array(z.strictObject({ poiId: idString, lapseOnlineDay: storyDay })),
  challenges: z.array(z.strictObject({ poiId: idString, untilOnlineDay: storyDay })),
  ships: z.array(
    z.strictObject({
      key: idString,
      role: z.enum(STORY_SHIP_ROLES),
      systemId: idString,
      missionId: z.enum(STORY_MISSION_IDS).optional(),
      agentSeq: z.number().int().min(-1),
    }),
  ),
  rigs: nonNegInt,
  tethysIncomeHead: nonNegInt,
  charterWaiver: z.strictObject({ charters: nonNegInt, waiveMinStanding: z.boolean() }),
  holdfastClaimsRegistered: z.boolean(),
  hulks: z.array(nonNegInt).optional(),
  cols: b64Cols,
}) satisfies z.ZodType<StorySave>
